// D:\Casticart\frontend\src\components\admin\chats\EventChatPage.js
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useMaintenance, MAINTENANCE_MODES } from '../../contexts/MaintenanceContext';
import { ADMIN_MESSAGE_FORMATS } from '../../constants/messageFormats';
import { SOCKET_EVENTS } from '../../lib/socketEvents';
import { clearActiveChatContext, setActiveChatContext } from '../../utils/activeChatContext';
import '../../css/admin/chats/EventChatPage.css';
import '../../css/common/chat/ChatTargetListRow.css';
import { useMessageCount } from '../../contexts/MessageCountContext';
import { useToast } from '../../components/common/ToastSystem';
import { getFileIconClass } from '../../components/common/chat/composer/fileIconClass';
import {
  sidebarTimeLabelJST,
  isWithinHoursFromNow
} from '../../utils/datetime';
import useChatDragDrop from '../../components/common/chat/composer/useChatDragDrop';
import ChatMentionTaskResolutionModal from '../../components/common/chat/mentions/ChatMentionTaskResolutionModal';
import useChatMentionTaskResolution from '../../components/common/chat/mentions/useChatMentionTaskResolution';
import ChatMessageSelectableCopyArea from '../../components/common/chat/timeline/ChatMessageSelectableCopyArea';
import EventNoteActionButton from '../../components/common/chat/notes/EventNoteActionButton';
import { sendImages } from '../../components/common/chat/shared/chatApi';
import useChatNotesBadgeCount from '../../components/common/chat/notes/useChatNotesBadgeCount';
import useChatAnnouncements from '../../components/common/chat/announcements/useChatAnnouncements';
import useEventChatAssetBadges from '../../components/common/chat/shared/useEventChatAssetBadges';
import { useEventAssignmentConfirmModal } from '../../components/common/events/EventAssignmentConfirmModal';
import useDelayedVisibility from '../../components/common/chat/shared/useDelayedVisibility';
import useChatMessageReactions from '../../components/common/chat/reactions/useChatMessageReactions';
import useChatMessageReadStatus from '../../components/common/chat/reactions/useChatMessageReadStatus';
import useViewportBoundedMenuPosition from '../../components/common/chat/shared/useViewportBoundedMenuPosition';
import { focusChatAnchorMessage } from '../../components/common/chat/timeline/chatMessageAnchorUtils';
import { supportsChatMessageReaction } from '../../components/common/chat/reactions/chatMessageReactionUtils';
import {
  hasCopyableChatMessageText,
  resolveCopyableChatMessageText,
  writeTextToClipboard,
} from '../../components/common/chat/timeline/chatMessageCopyUtils';
import {
  buildToolbarFilterChips,
  formatSearchChipDate,
} from '../../components/common/chat/search/chatTargetSearchChipUtils';
import { buildImageGroupDisplayMessages } from '../../components/common/chat/composer/chatImageGroupUtils';
import {
  patchMessageReadCountsByIds,
  pickRealtimeReadCount,
} from '../../components/common/chat/reactions/readReceiptRealtimeUtils';
import { createEventAvatarRenderers } from '../../components/common/chat/avatars/eventAvatarRenderers';
import {
  buildEventAnnouncementChecks,
  parseEventTimetableRuntimePayload,
  resolveEventTimetableRuntimeVisibility,
} from '../../components/common/chat/timeline/eventSystemMessageUtils';
import {
  buildEventAdminParticipants,
  buildEventArtistParticipants,
  buildEventConciergeParticipants,
  groupEventParticipantsByPart,
  resolveEventScopedPartMeta,
} from '../../components/common/chat/members/eventParticipantsPanelDataUtils';
import useEventParticipantPartScope from '../../components/common/chat/members/useEventParticipantPartScope';
import {
  resolveEventAnnouncementIconClass as getAnnouncementIconClass,
  isEventAnnouncementMessage as isAnnouncementMessage,
  isEventFinishedMessage,
  isEventImagesAddedMessage,
  isEventInfoPublishedMessage,
  isEventInfoUpdatedMessage,
  isEventMaterialSharedMessage,
  isEventMembersPublishedMessage,
  isStageSeatingSharedMessage,
  isEventNoteCreateMessage as isNoteCreateMessage,
  isEventNoteDeleteMessage as isNoteDeleteMessage,
  isEventNoteMessage as isNoteMessage,
  isEventNoteUpdateMessage as isNoteUpdateMessage,
  isEventSimpleAnnouncementMessage as isSimpleAnnouncementMessage,
  isEventTimetableCreatedMessage as isTimetableCreatedMessage,
  isEventTimetableMemoMessage as isTimetableMemoMessage,
  isEventTimetableRuntimeMessage as isTimetableRuntimeMessage,
  isEventTimetableUpdatedMessage as isTimetableUpdatedMessage,
} from '../../components/common/chat/timeline/eventMessageFormatUtils';
import {
  resolveEventAnnouncementSenderName as getAnnouncementSenderName,
  resolveEventMessageSourceBadgeLabel as resolveMessageSourceBadgeLabel,
  shouldShowEventMessageSourceBadge,
} from '../../components/common/chat/timeline/eventMessageSourceUtils';
import { shouldExcludeAdminSenderForMessage } from '../../components/common/chat/timeline/adminSenderExclusionUtils';
import { createEventMessageRenderPipeline } from '../../components/common/chat/timeline/eventMessageRenderPipeline';
import { openExternalUrl } from '../../utils/externalLink';
import { formatAdminDisplayName } from '../../utils/adminDisplayName';
import { createEventPipelineOptions } from '../../components/common/chat/eventlist/eventPipelineConfigUtils';
import { createEventParticipantsPanelProps } from '../../components/common/chat/members/eventParticipantsPanelConfigUtils';
import useChatProfileModalState from '../../components/common/chat/members/useChatProfileModalState';
import {
  EVENT_CHAT_MODE_GROUP_UNIFIED as CHAT_MODE_GROUP_UNIFIED,
  normalizeEventPerformanceTypeForThread,
  resolveExplicitEventChatMode,
  resolveExplicitEventGroupCount,
  resolveEventChatMode,
  resolveEventGroupCount,
  resolveEventThreadId,
} from '../../components/common/chat/eventlist/eventThreadMetaUtils';
import {
  appendVariant,
} from '../../components/common/chat/composer/chatAssetUtils';
import {
  normalizeEventUuidList,
  normalizePartLabel,
} from '../../components/common/chat/members/memberEventPartUtils';
import {
  parsePartIds,
  pickFirstTrimmedString as pickFirstString,
  toNum,
} from '../../components/common/chat/members/memberListUtils';
import {
  buildParticipantEventPartSummary as buildEventParticipantEventPartSummary,
} from '../../components/common/chat/members/eventParticipantUtils';
import {
  createEventProfilePayloadBuilders,
} from '../../components/common/chat/members/eventProfilePayloadUtils';
import { createEventAvatarProfileTargetResolver } from '../../components/common/chat/avatars/eventAvatarProfileTargetUtils';
import {
  mergeEventSidebarRow,
  normalizeGroupEventRows,
} from '../../components/common/chat/eventlist/eventGroupEventUtils';
import { joinEventRooms } from '../../components/common/chat/eventlist/eventSocketRoomUtils';
import {
  applySearchResultEvents,
  resolveEventListModeState,
} from '../../components/common/chat/eventlist/eventListStateUtils';
import {
  buildRealtimeEventSidebarPatch,
  upsertEventSidebarRowByUuid,
} from '../../components/common/chat/sidepanel/eventSidebarRowStateUtils';
import { composeAdminEventRowWithDetail } from '../../components/common/chat/eventlist/eventListComposeUtils';
import { loadEventListRows } from '../../components/common/chat/eventlist/eventListFetchUtils';
import {
  loadMoreEventList,
  observeEventListLoadMoreScroll,
  observeEventListLoadMoreSentinel,
  scheduleEventListRefreshTop,
  shouldLoadMoreEventList,
  triggerEventListLoadMoreIfNeeded,
} from '../../components/common/chat/eventlist/eventListInteractionUtils';
import {
  cleanupTimeoutRefs,
  shouldMarkLatestAsRead,
} from '../../components/common/chat/eventlist/eventChatEffectUtils';
import {
  applyEventTargetSearchDraft,
  clearEventTargetSearch,
  countActiveEventSearchFilters,
  loadEventSearchRowsWithUnread,
  openEventTargetSearchModal,
  resolveEventSearchCriteria,
} from '../../components/common/chat/search/eventSearchUtils';
import { linkifyTextNodes } from '../../components/common/chat/timeline/linkifyTextNodes';
import { normalizeStageSeatingShareUrlForAdmin } from '../../components/common/chat/stage-seating/stageSeatingShareLinkUtils';
import {
  buildChatMentionCandidates,
  normalizeMentionTargetId,
  normalizeMentionTargetRole,
  normalizeMentionTargetType,
} from '../../components/common/chat/mentions/chatMentionUtils';
import ChatWorkspaceLayout from '../../components/common/chat/shared/ChatWorkspaceLayout';
import '../../css/common/chat/ChatDragDrop.css';
import { ellipsizeFileName } from '../../components/common/chat/composer/fileNameUtils';
import { resolveAdminPermission } from '../../constants/adminPermissionCatalog';
import { resolveEventDetailPermission } from '../../constants/eventDetailPermissions';
import { getAdminSession } from '../../api/auth';
import {
  listAdminPendingMentionTargets,
  resolveAdminMentionTarget,
} from '../../api/adminChat';
import { listParts } from '../../api/artists';
import {
  getCastingSummaries,
  getEventDetail as getEventDetailFromApi,
  getEventTaskSummaries,
  getEventTicketSalesSummary,
} from '../../api/taskEvents';
import {
  listCommonEventCastingProgress,
  listEventCastingInfo,
} from '../../api/eventDetails';
import {
  deleteEventChatMessage,
  getAdminEventChatNotifyMute,
  getAdminEventChatSidebarRow,
  getEventChatMessagesAround,
  listEventChatMessages,
  listEventChatNewMessages,
  markEventChatMessagesRead,
  sendEventChatFileMessage,
  updateAdminEventChatNotifyMute,
} from '../../api/eventChat';
import {
  CHAT_FOLLOWUP_AI_TARGET_ID,
  useAdminChatFollowupSummary,
} from '../../components/common/chat/followups/chatFollowupApi';
import EventChatSidebar from '../../components/admin/chats/eventChatPageSidebar';
import {
  EventChatSearchModal,
  EventChatSettingsModal,
  EventAlbumModal,
  EventMaterialsModal,
  EventMentionSilenceConfirmModal,
} from '../../components/admin/chats/eventChatPageModals';
import { EventChatSidePanel } from '../../components/admin/chats/eventChatPageParts';
import EventChatHeader from '../../components/admin/chats/eventChatPageHeader';
import ChatLinksModal from '../../components/common/chat/assets/ChatLinksModal';
import ChatMediaModal from '../../components/common/chat/assets/ChatMediaModal';
import { eventChatAssetSource } from '../../api/chatAssets';
import EventChatConversation from '../../components/admin/chats/eventChatPageConversation';
import useEventChatSidePanelData from '../../components/admin/chats/useEventChatSidePanelData';
import EventDerivedGroupLauncher from '../../components/admin/chats/EventDerivedGroupLauncher';
import { nowJstDbString } from './eventChatPageDateUtils';

/* =========================================================
 * 設定 / 共通ユーティリティ
 * =======================================================*/
import {
  dlog,
  wait,
  API_URL,
  ADMIN_EVENTS_HEAD_URL,
  ADMIN_EVENTS_PAGE_URL,
  ADMIN_PARTICIPANTS_URL,
  ADMIN_GROUP_EVENTS_URL,
  ADMIN_REPRESENTATIVE_EVENT_BY_THREAD_URL,
  EVENT_NOTIFICATION_SILENCE_URL,
  ADMIN_SEARCH_API,
  buildAdminEventNotesListUrl,
  MESSAGE_FILE_PATH,
  ADMIN_PROFILE_IMAGE_PATH,
  ARTIST_PROFILE_IMAGE_PATH,
  EARLIEST_CREATED_AT,
  STALE_MS,
  EVENT_ASSIGNEE_SEARCH_STORAGE_KEY,
  UNSENDABLE_FORMATS,
  isActiveNotificationSilenceWindow,
  fetchJsonSafe,
  normalizePartsArray,
  buildAssetUrl,
  buildMessageImageUrl,
  applyLandscapeContain,
  formatExpireDateLabel,
  socket,
  pushViewState,
  replaceViewState,
} from '../../components/admin/chats/eventChatPageHelpers';
import { mergeMessages, patchMessages, removeMessages } from '../../components/common/chat/timeline/timelineMerge';
import useAdminEventChatPermissions from './useEventChatPageElement.permissions';
import useAdminEventChatParticipants from './useEventChatPageElement.participants';
import useAdminEventChatListState from './useEventChatPageElement.list';
import useAdminEventChatMessages from './useEventChatPageElement.messages';
import useAdminEventChatRuntime from './useEventChatPageElement.runtime';

export default function useEventChatPageElement() {
  const ACTIVE_CHAT_OWNER = 'admin:event-chat';
  const navigate = useNavigate();
  const location = useLocation();
  const { user, fetchSession, checking } = useAuth();
  const { success, error: toastError, info } = useToast();
  const { confirmAssignmentForEvents, assignmentConfirmModal } = useEventAssignmentConfirmModal({
    currentAdmin: user,
  });
  const { maintenance, maintenanceMode } = useMaintenance();
  const isReadOnly =
    maintenanceMode === MAINTENANCE_MODES.READ_ONLY || (maintenance && maintenance.readOnly);
  const __params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialThreadId = __params.get('thread');
  const initialEventId = __params.get('event');
  const initialView    = (__params.get('view') || 'chat');
  const initialAnchorMessageId =
    __params.get('message') || __params.get('messageId') || __params.get('message_id') || '';
  const hasInitialChatTarget = Boolean(initialThreadId || initialEventId);

  /* 一覧 / 選択状態 */
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [isAiTargetSelected, setIsAiTargetSelected] = useState(() => !hasInitialChatTarget);
  const [followupRefreshKey, setFollowupRefreshKey] = useState(0);
  const isAiTargetSelectedRef = useRef(!hasInitialChatTarget);
  const {
    summary: followupSummary,
    badgeCount: followupBadgeCount,
  } = useAdminChatFollowupSummary();
  const [selectedEventInfo, setSelectedEventInfo] = useState(null);
  const [sidebarDetails, setSidebarDetails] = useState(null);
  const [, setIsSidebarLoading] = useState(false);
  const [groupEventSidebarRows, setGroupEventSidebarRows] = useState([]);
  const [groupPanelEvents, setGroupPanelEvents] = useState([]);
  const [pendingMentionTargets, setPendingMentionTargets] = useState({});
  const [resolvingMentionTargetId, setResolvingMentionTargetId] = useState('');

  const refreshPendingMentionTargets = useCallback(async () => {
    try {
      const data = await listAdminPendingMentionTargets({ scope: 'event' });
      const next = {};
      (Array.isArray(data?.targets) ? data.targets : []).forEach((target) => {
        const id = String(target?.targetId || '').trim();
        if (!id) return;
        next[id] = {
          count: Number(target?.count || 0),
          messageId: target?.messageId || target?.message_id || null,
          latestCreatedAt: target?.latestCreatedAt || null,
        };
      });
      setPendingMentionTargets(next);
    } catch {
      setPendingMentionTargets({});
    }
  }, []);

  const resolveMentionTarget = useCallback(async (targetId) => {
    const normalizedTargetId = String(targetId || '').trim();
    if (!normalizedTargetId) return;
    setResolvingMentionTargetId(normalizedTargetId);
    try {
      await resolveAdminMentionTarget({ scope: 'event', targetId: normalizedTargetId });
      setPendingMentionTargets((current) => {
        const next = { ...current };
        delete next[normalizedTargetId];
        return next;
      });
    } catch {
      toastError('メンション対応状態の更新に失敗しました。');
    } finally {
      setResolvingMentionTargetId('');
    }
  }, [toastError]);

  const updatePendingMentionTargetCount = useCallback((scope, targetId, delta) => {
    if (scope !== 'event') return;
    const normalizedTargetId = String(targetId || '').trim();
    if (!normalizedTargetId) return;
    setPendingMentionTargets((current) => {
      const currentTarget = current[normalizedTargetId];
      const nextCount = Math.max(0, Number(currentTarget?.count || 0) + Number(delta || 0));
      const next = { ...current };
      if (nextCount <= 0) {
        delete next[normalizedTargetId];
      } else {
        next[normalizedTargetId] = { ...currentTarget, count: nextCount };
      }
      return next;
    });
  }, []);

  const {
    modalProps: mentionResolutionModalProps,
    loadingTargetKey: loadingMentionResolutionTargetKey,
    openMentionResolution,
  } = useChatMentionTaskResolution({
    actorType: 'admin',
    onTargetCountChange: updatePendingMentionTargetCount,
    onError: toastError,
  });

  useEffect(() => {
    refreshPendingMentionTargets();
  }, [refreshPendingMentionTargets]);

  /* メッセージ */
  const [messages, setMessages] = useState([]);
  const [replyDraft, setReplyDraft] = useState(null);
  const [messageMenu, setMessageMenu] = useState(null);
  const [inlineCopyMessageId, setInlineCopyMessageId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    setInlineCopyMessageId('');
  }, [selectedEventId]);
  useEffect(() => {
    isAiTargetSelectedRef.current = isAiTargetSelected;
  }, [isAiTargetSelected]);
  useEffect(() => {
    selectedEventIdRef.current = selectedEventId;
    if (selectedEventId && pendingRestoreRef.current) {
      pendingRestoreRef.current = false;
    }
  }, [selectedEventId]);
  useEffect(() => {
    setReplyDraft(null);
  }, [selectedEventId]);
  const handleUploading = useCallback((flag) => {
    if (typeof flag === 'boolean') { setIsUploading(flag); return; }
    if (flag && typeof flag === 'object') {
      const { active, kind } = flag;
      setIsUploading(!!active && kind !== 'text');
      return;
    }
    setIsUploading(!!flag);
  }, []);

  /* ページング・状態 */
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [messageWindowOffset, setMessageWindowOffset] = useState(0);
  const [hasNewerMessages, setHasNewerMessages] = useState(false);
  const [loadingNewerMessages, setLoadingNewerMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [activeAnchorMessageId, setActiveAnchorMessageId] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [loadedMessagesEventId, setLoadedMessagesEventId] = useState('');
  const showDelayedInitialLoading = useDelayedVisibility(isInitialLoading, 1000);

  /* マスタ・ログイン情報 */
  const [partsList, setPartsList] = useState([]);
  const [castingInfo, setCastingInfo] = useState([]);
  const [taskSummary, setTaskSummary] = useState(null);
  const [ticketSummary, setTicketSummary] = useState(null);
  const [isTicketSummaryLoading, setIsTicketSummaryLoading] = useState(false);
  const [groupCastingSummaryMap, setGroupCastingSummaryMap] = useState({});
  const [groupTaskSummaryMap, setGroupTaskSummaryMap] = useState({});
  const [groupTicketSummaryMap, setGroupTicketSummaryMap] = useState({});

  const {
    canUseEventChat,
    canUseEventChatForSession,
    canViewCastingInfo,
    canViewCastingProgress,
    canCreateDerivedGroupChat,
    canViewTicketSales,
    canViewEventTasks,
    canViewEventNotes,
    canManageEventNotes,
    canViewMaterials,
    canEditMaterials, canDownloadMaterials,
    canViewMaterialDownloadLogs,
    canViewAlbum,
    canEditAlbum,
    canDownloadAlbum,
  } = useAdminEventChatPermissions({
    resolveAdminPermission,
    resolveEventDetailPermission,
    user,
  });

  const [participantArtists, setParticipantArtists] = useState([]);
  const [participantSnapshot, setParticipantSnapshot] = useState(null);
  const participantArtistMap = useMemo(() => {
    const map = new Map();
    participantArtists.forEach((artist) => {
      if (artist?.artist_id == null) return;
      map.set(String(artist.artist_id), artist);
    });
    return map;
  }, [participantArtists]);
  const {
    profileTarget,
    setProfileTarget,
    closeProfileModal,
    openProfileTarget,
  } = useChatProfileModalState();
  const {
    markEventAsRead,
    refreshEventChatUnread,
    setEventChatMessageCount,
    eventUnreadMapAdmin,
    setAdminEventChatTone,
    adminEventChatTone
  } = useMessageCount();

  /* 管理者セッション */
  const [adminSession, setAdminSession] = useState(null);
  const [adminSessionLoaded, setAdminSessionLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const s = await getAdminSession();
        setAdminSession(s);
      } catch {
        setAdminSession(null);
      } finally {
        setAdminSessionLoaded(true);
      }
    })();
  }, []);
  const loggedInAdminId =
    adminSession?.userId ??
    adminSession?.user?.id ??
    adminSession?.id ??
    (user?.userType === 'admin' ? (user?.userId ?? user?.id ?? null) : null);
  const normalizeAdminId = (value) => (value == null ? '' : String(value));
  const isAdminAssignedToEvent = useCallback(
    (evt) => {
      const adminId = normalizeAdminId(loggedInAdminId);
      if (!adminId) return false;
      const assigned =
        Array.isArray(evt?.assigned_admins) && evt.assigned_admins.length
          ? evt.assigned_admins
          : eventDetailCacheRef.current.get(evt?.event_uuid)?.assigned_admins;
      if (!Array.isArray(assigned) || assigned.length === 0) return false;
      return assigned.some((admin) => {
        const id =
          admin?.admin_user_id ??
          admin?.id ??
          admin?.user_id ??
          admin?.userId ??
          admin?.adminId ??
          admin?.admin_userid;
        if (id == null) return false;
        return String(id) === adminId;
      });
    },
    [loggedInAdminId],
  );
  const resolveEventUnread = useCallback(
    (evt) => {
      if (!evt) return 0;
      const eventId = evt.event_uuid ?? evt.event_id ?? evt.id;
      if (eventId == null) return 0;
      if (eventUnreadMapAdmin instanceof Map) {
        if (eventUnreadMapAdmin.has(eventId)) {
          return Number(eventUnreadMapAdmin.get(eventId) || 0);
        }
      } else if (eventUnreadMapAdmin && Object.prototype.hasOwnProperty.call(eventUnreadMapAdmin, eventId)) {
        return Number(eventUnreadMapAdmin[eventId] || 0);
      }
      const fallback = evt.unreadCount ?? evt.unread_count;
      return Number(fallback || 0);
    },
    [eventUnreadMapAdmin],
  );

  const [latestCreatedAt, setLatestCreatedAt] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMaterialsModal, setShowMaterialsModal] = useState(false);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [chatHeaderMenuOpen, setChatHeaderMenuOpen] = useState(false);
  const [chatHeaderMenuPosition, setChatHeaderMenuPosition] = useState(null);
  const {
    materialBadge,
    albumBadge,
    hasNew: hasNewEventAssets,
    hasAny: hasAnyEventAssets,
    markMaterialsSeen,
    markAlbumSeen,
  } = useEventChatAssetBadges({
    eventId: selectedEventId,
    viewerKey: `admin:${loggedInAdminId || 'unknown'}`,
  });
  const assetMenuBadgeTone = hasNewEventAssets ? 'new' : hasAnyEventAssets ? 'has' : 'none';
  const openMaterialsModal = useCallback(() => {
    markMaterialsSeen();
    setShowMaterialsModal(true);
  }, [markMaterialsSeen]);
  const openAlbumModal = useCallback(() => {
    markAlbumSeen();
    setShowAlbumModal(true);
  }, [markAlbumSeen]);
  const openLinksModal = useCallback(() => setShowLinksModal(true), []);
  const openMediaModal = useCallback(() => setShowMediaModal(true), []);
  const chatAssetSource = useMemo(
    () => (selectedEventId ? eventChatAssetSource(selectedEventId) : null),
    [selectedEventId],
  );
  const [settingsEvent, setSettingsEvent] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [muteSetting, setMuteSetting] = useState(true);
  const [mentionSilenceConfirm, setMentionSilenceConfirm] = useState(null);
  const mentionSilenceConfirmResolverRef = useRef(null);
  const closeChatHeaderMenu = useCallback(() => {
    setChatHeaderMenuOpen(false);
    setChatHeaderMenuPosition(null);
  }, []);
  const toggleChatHeaderMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (chatHeaderMenuOpen) {
      closeChatHeaderMenu();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    setChatHeaderMenuPosition({
      left: Math.min(Math.max(12, rect.right - menuWidth), window.innerWidth - menuWidth - 12),
      top: Math.min(rect.bottom + 8, window.innerHeight - 96),
    });
    setChatHeaderMenuOpen(true);
  }, [chatHeaderMenuOpen, closeChatHeaderMenu]);
  useEffect(() => {
    if (!chatHeaderMenuOpen) return undefined;
    const close = () => closeChatHeaderMenu();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [chatHeaderMenuOpen, closeChatHeaderMenu]);
  useEffect(() => {
    latestCreatedAtRef.current = latestCreatedAt;
  }, [latestCreatedAt]);
  const {
    selectedEventAdmins,
    isConciergeEvent,
    conciergePlanner,
    adminLookup,
    isSelectedGroupUnified,
    isSettingsGroupUnified,
    effectiveGroupEvents,
    groupEventDetailRows,
    participantGroupEventOrderMap,
    participantGroupEventMap,
    buildParticipantEventMembershipLabel,
    eventSchedule,
    eventHeaderSchedule,
    headerPerformanceType,
    headerNextPerformance,
    isSelectedMultiPerformanceEvent,
    headerNextMultiPerformance,
    participantCounts,
    hasConciergeParticipants,
    castingPct,
    ticketProgress,
    taskProgress,
    scheduleDetail,
    venueInfo,
    sidebarNote,
    selectedEventFlyerUrl,
    selectedEventFlyerBackUrl,
    selectedEventFlyerFullUrl,
    selectedEventFlyerBackFullUrl,
    handleGoHeaderEventDetail,
    eventDetailUrl,
    venueIdFromData,
    venueLink,
    venueResolvedAddress,
    venueResolvedName,
    mapLinkUrl,
  } = useEventChatSidePanelData({
    events,
    selectedEventId,
    selectedEventInfo,
    sidebarDetails,
    participantSnapshot,
    groupPanelEvents,
    groupEventSidebarRows,
    settingsEvent,
    canViewEventTasks,
    canViewCastingProgress,
    canViewTicketSales,
    groupTaskSummaryMap,
    groupCastingSummaryMap,
    groupTicketSummaryMap,
    castingInfo,
    ticketSummary,
    taskSummary,
    navigate,
  });

  /* モーダル等 */
  const [showMembersModal, setShowMembersModal] = useState(false);
  const derivedGroupLauncherRef = useRef(null);
  const {
    announcements,
    latestAnnouncement,
    announcementCount,
    listOpen: isAnnouncementListOpen,
    setListOpen: setIsAnnouncementListOpen,
    refreshAnnouncements,
    pinAnnouncement,
    unpinAnnouncement,
    isAnnouncedMessage,
    actionMessageId: announcementActionMessageId,
  } = useChatAnnouncements({
    mode: 'admin',
    scope: 'event',
    eventId: selectedEventId || '',
    enabled: Boolean(selectedEventId && !isAiTargetSelected),
    onError: toastError,
  });
  const {
    detailOpen: isReactionDetailOpen,
    detailGroups: reactionDetailGroups,
    detailLoading: reactionDetailLoading,
    openReactionDetail,
    closeReactionDetail,
    toggleReaction,
    refreshMessageReactionDetails,
    isReactionPending,
  } = useChatMessageReactions({
    mode: 'admin',
    scope: 'event',
    eventId: selectedEventId || '',
    enabled: Boolean(selectedEventId && !isAiTargetSelected),
    setMessages,
    onError: toastError,
  });
  const {
    detailOpen: isReadStatusDetailOpen,
    detailLoading: readStatusDetailLoading,
    detailReaders: readStatusDetailReaders,
    openReadStatusDetail,
    closeReadStatusDetail,
  } = useChatMessageReadStatus({
    scope: 'event',
    eventId: selectedEventId || '',
    enabled: Boolean(selectedEventId && !isAiTargetSelected),
    onError: toastError,
  });
  const [showNotes, setShowNotes] = useState(false);
  const { noteCount, refreshNoteCount } = useChatNotesBadgeCount({
    ownerId: selectedEventId,
    buildListUrl: buildAdminEventNotesListUrl,
    enabled: Boolean(selectedEventId && canViewEventNotes),
  });
  useEffect(() => {
    if (!canViewEventNotes && showNotes) {
      setShowNotes(false);
    }
  }, [canViewEventNotes, showNotes]);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  useEffect(() => {
    showNotesRef.current = showNotes;
  }, [showNotes]);
  useEffect(() => {
    const currentId = settingsEvent?.event_uuid || settingsEvent?.eventId || null;
    settingsEventIdRef.current = currentId;
    settingsEventRef.current = settingsEvent;
  }, [settingsEvent]);

  /* イベント一覧ページング */
  const EVENTS_PAGE_LIMIT = 20;
  const [eventsOffset, setEventsOffset] = useState(0);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [loadMoreNode, setLoadMoreNode] = useState(null);
  const [eventListNode, setEventListNode] = useState(null);
  const eventListLoadMorePendingRef = useRef(false);
  const eventListForceFallbackExhaustedRef = useRef(false);
  const eventListRetryTimerRef = useRef(null);

  /* 新着バナー */
  const [showNewMsgBanner, setShowNewMsgBanner] = useState(false);
  const [bannerMsg, setBannerMsg] = useState(null);
  const bannerTimeoutRef = useRef(null);
  const lastBannerIdRef = useRef(null);

  /* 参照系 */
  const limit = 20;
  const chatContainerRef = useRef(null);
  const eventChatAreaRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const sidebarPressRef = useRef({
    suppressClick: false,
    suppressContextMenu: false,
    timer: null,
    contextMenuTimer: null,
  });
  const settingsEventIdRef = useRef(null);
  const settingsEventRef = useRef(null);
  const messageIdsRef = useRef(new Set());
  const oldestCreatedAtRef = useRef(null);
  const selfReadGuardRef = useRef(0);
  const pendingReadReceiptKeysRef = useRef(new Set());
  const completedReadReceiptKeysRef = useRef(new Set());
  const swipeStateRef = useRef(null);
  const initialLoadRef = useRef(new Set());
  const anchorHighlightTimerRef = useRef(null);

  const initialAppliedRef = useRef(false);
  const reconcileTimerRef = useRef(null);
  const RECON_LIMIT = 50;
  const refreshTopTimerRef = useRef(null);
  const eventDetailCacheRef = useRef(new Map());
  const eventDetailInFlightRef = useRef(new Map());
  const shouldSubtractSelf = selectedEventInfo ? isAdminAssignedToEvent(selectedEventInfo) : false;
  const joinedRoomsRef = useRef(new Set());
  const currentRoomRef = useRef(null);
  const lastDiffFetchRef = useRef(Date.now());
  const messagesRef = useRef([]);
  const selectedEventIdRef = useRef(null);
  const selectedThreadIdRef = useRef(null);
  const showNotesRef = useRef(false);
  const isEventChatAreaOpenRef = useRef(false);
  const isSearchModeRef = useRef(false);
  const orderFrozenRef = useRef(false);
  const latestCreatedAtRef = useRef(null);
  const fetchMessagesRef = useRef(null);
  const fetchNewMessagesRef = useRef(null);
  const findPartLabelByIdRef = useRef(null);
  const pendingReadEventIdsRef = useRef(new Map());

  const prunePendingReadEvents = useCallback(() => {
    const now = Date.now();
    for (const [eventId, expiresAt] of pendingReadEventIdsRef.current.entries()) {
      if (!Number.isFinite(expiresAt) || expiresAt <= now) {
        pendingReadEventIdsRef.current.delete(eventId);
      }
    }
  }, []);

  const markEventPendingRead = useCallback((eventId) => {
    const normalizedEventId = String(eventId || '').trim();
    if (!normalizedEventId) return;
    pendingReadEventIdsRef.current.set(normalizedEventId, Date.now() + 1500);
  }, []);

  const clearPendingEventRead = useCallback((eventId) => {
    const normalizedEventId = String(eventId || '').trim();
    if (!normalizedEventId) return;
    pendingReadEventIdsRef.current.delete(normalizedEventId);
  }, []);

  const shouldSuppressEventUnread = useCallback((eventId) => {
    const normalizedEventId = String(eventId || '').trim();
    if (!normalizedEventId) return false;
    prunePendingReadEvents();
    return (
      pendingReadEventIdsRef.current.has(normalizedEventId) ||
      (
        isEventChatAreaOpenRef.current &&
        String(selectedEventIdRef.current || '').trim() === normalizedEventId
      )
    );
  }, [prunePendingReadEvents]);

  const markEventUnreadLocally = useCallback((eventId) => {
    const normalizedEventId = String(eventId || '').trim();
    if (!normalizedEventId) return;
    markEventPendingRead(normalizedEventId);
    setEvents((prev) =>
      prev.map((eventRow) =>
        eventRow.event_uuid === normalizedEventId
          ? { ...eventRow, unreadCount: 0, unread_count: 0 }
          : eventRow,
      ),
    );
    markEventAsRead(normalizedEventId);
  }, [markEventAsRead, markEventPendingRead]);

  const resolveThreadIdByEvent = useCallback((eventUuid) => {
    if (!eventUuid) return null;
    if (selectedEventInfo?.event_uuid === eventUuid) {
      const threadId = resolveEventThreadId(selectedEventInfo);
      if (threadId) return threadId;
    }
    if (sidebarDetails?.event_uuid === eventUuid) {
      const threadId = resolveEventThreadId(sidebarDetails);
      if (threadId) return threadId;
    }
    const row = (events || []).find((item) => item?.event_uuid === eventUuid);
    return resolveEventThreadId(row);
  }, [events, selectedEventInfo, sidebarDetails]);

  useEffect(() => {
    selectedThreadIdRef.current = resolveThreadIdByEvent(selectedEventId || selectedEventIdRef.current);
  }, [selectedEventId, resolveThreadIdByEvent]);

  const shouldShowSidePanel = !isAiTargetSelected && Boolean(selectedEventId && selectedEventInfo);
  const isSidePanelLayoutOpen = shouldShowSidePanel && isSidePanelOpen;

  const [imgModalOpen, setImgModalOpen] = useState(false);
  const [imgModalIndex, setImgModalIndex] = useState(0);
  const prevHistoryForChatImageRef = useRef(null);

  /* 検索 */
  const [searchQuery, setSearchQuery] = useState('');
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  // 検索結果は50件ずつ取得し、スクロールで過去分を追加読み込みする
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchNextOffset, setSearchNextOffset] = useState(0);
  const [searchSnapshotToken, setSearchSnapshotToken] = useState('');
  const [isSearchLoadingMore, setIsSearchLoadingMore] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchModalError, setSearchModalError] = useState('');
  const [searchDraft, setSearchDraft] = useState({
    eventQuery: '',
    messageQuery: '',
    startDate: '',
    endDate: '',
  });
  useEffect(() => {
    isSearchModeRef.current = isSearchMode;
  }, [isSearchMode]);

  /* ソケット部屋（管理者） */
  useEffect(() => {
    try {
      if (!socket.connected) socket.connect();
      socket.emit('join_admins');
    } catch {}
  }, []);

  /* 並べ替え凍結（初期描画安定化） */
  const [orderFrozen, setOrderFrozen] = useState(true);
  useEffect(() => {
    orderFrozenRef.current = orderFrozen;
  }, [orderFrozen]);
  useEffect(
    () => () => {
      if (anchorHighlightTimerRef.current) {
        clearTimeout(anchorHighlightTimerRef.current);
        anchorHighlightTimerRef.current = null;
      }
    },
    [],
  );

  /* ======== モバイル表示制御（≤700px） ========
     - isNarrow: 画面幅が 700px 以下か
     - mobileView: 'list' | 'chat'
       * 'list'  … イベント一覧のみ全画面
       * 'chat'  … チャットのみ全画面（戻るボタン表示）
  */
  const initialMobileView = useMemo(() => {
    if (typeof window === 'undefined') return 'list';
    const narrow = window.matchMedia && window.matchMedia('(max-width: 700px)').matches;
    if (!narrow) return 'list';
    try {
      const params = new URLSearchParams(location.search || '');
      if (params.get('thread') || params.get('event')) return 'chat';
    } catch {}
    return 'list';
  }, [location.search]);
  const pendingRestoreRef = useRef(initialMobileView === 'chat');
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(max-width: 700px)').matches;
  });
  const [mobileView, setMobileView] = useState(initialMobileView);
  const isEventChatAreaOpen = Boolean(
    selectedEventId &&
      !isAiTargetSelected &&
      !showNotes &&
      (!isNarrow || mobileView === 'chat'),
  );
  useEffect(() => {
    isEventChatAreaOpenRef.current = isEventChatAreaOpen;
  }, [isEventChatAreaOpen]);

  useEffect(() => {
    if (isEventChatAreaOpen) {
      setActiveChatContext({
        owner: ACTIVE_CHAT_OWNER,
        scope: 'admin',
        channel: 'event',
        eventId: selectedEventId,
        threadId: selectedThreadIdRef.current || resolveThreadIdByEvent(selectedEventId),
      });
      return () => clearActiveChatContext(ACTIVE_CHAT_OWNER);
    }
    clearActiveChatContext(ACTIVE_CHAT_OWNER);
    return () => clearActiveChatContext(ACTIVE_CHAT_OWNER);
  }, [isEventChatAreaOpen, selectedEventId, resolveThreadIdByEvent]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const listOnly = isNarrow && mobileView === 'list';
    window.dispatchEvent(new CustomEvent('casticart:chat-footer', { detail: { listOnly } }));
  }, [isNarrow, mobileView]);
  // モバイルで一覧表示を優先する操作があったか
  const mobileEventListPrefRef = useRef(initialMobileView === 'list');
  // モバイルチャット表示用に pushState 済みか
  const mobileEventChatHistoryRef = useRef(false);
  const goToEventList = useCallback(() => {
    setMobileView('list');
    mobileEventListPrefRef.current = true;
    mobileEventChatHistoryRef.current = false;
    replaceViewState(null, 'list');
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mm = window.matchMedia('(max-width: 700px)');
    const handler = (e) => {
      const narrow = e.matches;
      setIsNarrow(narrow);

      if (!narrow) {
        // 広くなったらデスクトップ表示（両方表示）へ復帰
        pendingRestoreRef.current = false;
        replaceViewState(selectedEventId, showNotes ? 'notes-list' : 'chat');
        return;
      }

      if (pendingRestoreRef.current && !selectedEventId) {
        setMobileView('chat');
        return;
      }

      // 狭いとき：選択済みかどうかと、一覧優先フラグで決定
      if (!selectedEventId && !isAiTargetSelectedRef.current) {
        setMobileView('list');
        replaceViewState(null, 'list');
        return;
      }

      setMobileView(mobileEventListPrefRef.current ? 'list' : 'chat');
    };
    handler(mm); // 初期同期
    mm.addEventListener ? mm.addEventListener('change', handler) : mm.addListener(handler);
    return () => {
      mm.removeEventListener ? mm.removeEventListener('change', handler) : mm.removeListener(handler);
    };
  }, [selectedEventId, showNotes]);

  // モバイル時の list/chat 自動切替（ユーザーの意思を尊重する）
  useEffect(() => {
    if (!isNarrow) return;
    if (pendingRestoreRef.current) return;

    if (!selectedEventId && !isAiTargetSelectedRef.current) {
      if (mobileView !== 'list') setMobileView('list');
      return;
    }

    // 一覧優先フラグが立っている状態で chat ビューになった場合は解除
    if (mobileView === 'chat' && mobileEventListPrefRef.current) {
      mobileEventListPrefRef.current = false;
      return;
    }

    if (mobileEventListPrefRef.current) {
      if (mobileView !== 'list') setMobileView('list');
    } else if (mobileView !== 'chat') {
      setMobileView('chat');
    }
  }, [isNarrow, mobileView, selectedEventId, isAiTargetSelected]);

  // モバイルでチャットビューを開いたとき、ブラウザバックで一覧に戻れるように履歴エントリを 1 回だけ追加
  useEffect(() => {
    if (!isNarrow) {
      mobileEventChatHistoryRef.current = false;
      return;
    }
    if (
      mobileView === 'chat' &&
      (selectedEventId || isAiTargetSelectedRef.current) &&
      !mobileEventListPrefRef.current &&
      !mobileEventChatHistoryRef.current
    ) {
      try {
        window.history.pushState(
          {
            scope: 'admin-event-chat',
            view: 'chat',
            eventId: selectedEventId || CHAT_FOLLOWUP_AI_TARGET_ID,
          },
          '',
          '',
        );
        mobileEventChatHistoryRef.current = true;
      } catch {
        mobileEventChatHistoryRef.current = false;
      }
    }
  }, [isNarrow, mobileView, selectedEventId, isAiTargetSelected]);

  useEffect(() => {
    const area = eventChatAreaRef.current;
    if (!area) return;

    const handleTouchStart = (e) => {
      if (!isNarrow || mobileView !== 'chat') {
        swipeStateRef.current = null;
        return;
      }
      const t = e.touches[0];
      swipeStateRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        locking: null,
        triggered: false,
      };
    };

    const handleTouchMove = (e) => {
      const data = swipeStateRef.current;
      if (!data || data.triggered || !isNarrow || mobileView !== 'chat') return;
      const t = e.touches[0];
      const dx = t.clientX - data.startX;
      const dy = t.clientY - data.startY;

      if (!data.locking) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          data.locking = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        } else {
          return;
        }
      }
      if (data.locking !== 'x') return;

      if (dx <= -80 && Math.abs(dy) < 60) {
        data.triggered = true;
        goToEventList();
      }
    };

    const clearSwipe = () => {
      swipeStateRef.current = null;
    };

    area.addEventListener('touchstart', handleTouchStart, { passive: true });
    area.addEventListener('touchmove', handleTouchMove, { passive: true });
    area.addEventListener('touchend', clearSwipe);
    area.addEventListener('touchcancel', clearSwipe);
    return () => {
      area.removeEventListener('touchstart', handleTouchStart);
      area.removeEventListener('touchmove', handleTouchMove);
      area.removeEventListener('touchend', clearSwipe);
      area.removeEventListener('touchcancel', clearSwipe);
    };
  }, [isNarrow, mobileView, goToEventList]);

  /* パート一覧（取得直後にサニタイズ） */
  useEffect(() => {
    listParts()
      .then(data => setPartsList(normalizePartsArray(data)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedEventId) {
      setParticipantSnapshot(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchJsonSafe(
          ADMIN_PARTICIPANTS_URL(selectedEventId),
          {},
          { label: '参加者情報の取得に失敗しました。' }
        );
        if (!cancelled) setParticipantSnapshot(payload || null);
      } catch {
        if (!cancelled) setParticipantSnapshot(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId || !canViewCastingProgress) {
      setParticipantArtists([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const json = await listCommonEventCastingProgress(selectedEventId);
        if (!cancelled) {
          setParticipantArtists(Array.isArray(json) ? json : []);
        }
      } catch {
        if (!cancelled) setParticipantArtists([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId, canViewCastingProgress]);

  useEffect(() => {
    if (!selectedEventId || !canViewCastingInfo) {
      setCastingInfo([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await listEventCastingInfo(selectedEventId);
        if (!cancelled) setCastingInfo(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setCastingInfo([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId, canViewCastingInfo]);

  useEffect(() => {
    if (!selectedEventId || !canViewEventTasks) {
      setTaskSummary(null);
      return;
    }
    let cancelled = false;
    getEventTaskSummaries({ eventUuids: [selectedEventId] })
      .then((summaries) => summaries?.[selectedEventId] || null)
      .then((summary) => {
        if (!cancelled) setTaskSummary(summary);
      })
      .catch(() => {
        if (!cancelled) setTaskSummary(null);
      });
    return () => { cancelled = true; };
  }, [canViewEventTasks, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId || !canViewTicketSales) {
      setTicketSummary(null);
      setIsTicketSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setIsTicketSummaryLoading(true);
    getEventTicketSalesSummary(selectedEventId)
      .then((summary) => {
        if (cancelled) return;
        setTicketSummary(summary);
      })
      .catch(() => {
        if (cancelled) return;
        setTicketSummary(null);
      })
      .finally(() => {
        if (!cancelled) setIsTicketSummaryLoading(false);
      });
    return () => { cancelled = true; };
  }, [canViewTicketSales, selectedEventId]);

  const {
    getMainPartLabel,
    buildParticipantEventPartSummary,
    buildArtistProfilePayload,
    buildAdminProfilePayload,
    mentionCandidates,
    closeMentionSilenceConfirm,
    handleBeforeEventChatSend,
    handleMentionClick,
    participantsPanelProps,
    eventDerivedGroupArtists,
  } = useAdminEventChatParticipants({
    ADMIN_PROFILE_IMAGE_PATH,
    ARTIST_PROFILE_IMAGE_PATH,
    EVENT_NOTIFICATION_SILENCE_URL,
    appendVariant,
    buildAssetUrl,
    buildChatMentionCandidates,
    buildEventAdminParticipants,
    buildEventArtistParticipants,
    buildEventConciergeParticipants,
    buildEventParticipantEventPartSummary,
    buildParticipantEventMembershipLabel,
    castingInfo,
    conciergePlanner,
    createEventParticipantsPanelProps,
    createEventProfilePayloadBuilders,
    effectiveGroupEvents,
    fetchJsonSafe,
    findPartLabelByIdRef,
    groupEventParticipantsByPart,
    hasConciergeParticipants,
    isActiveNotificationSilenceWindow,
    isConciergeEvent,
    isSelectedGroupUnified,
    loggedInAdminId,
    mentionSilenceConfirmResolverRef,
    normalizeEventUuidList,
    normalizeMentionTargetId,
    normalizeMentionTargetRole,
    normalizeMentionTargetType,
    normalizePartLabel,
    normalizePartsArray,
    openProfileTarget,
    parsePartIds,
    participantArtistMap,
    participantArtists,
    participantCounts,
    participantGroupEventMap,
    participantGroupEventOrderMap,
    participantSnapshot,
    partsList,
    pickFirstString,
    resolveEventScopedPartMeta,
    selectedEventAdmins,
    selectedEventId,
    selectedEventInfo,
    setMentionSilenceConfirm,
    setProfileTarget,
    setShowMembersModal,
    toNum,
    toastError,
    useEventParticipantPartScope,
  });

  const isMyAdminMessage = useCallback(
    (m) =>
      !!m &&
      !shouldExcludeAdminSenderForMessage(m) &&
      m.message_type === 'admin_message' &&
      String(m.admin_user_id) === String(loggedInAdminId),
    [loggedInAdminId]
  );

  const canUnsendMessage = useCallback(
    (message) => {
      if (isReadOnly) return false;
      if (!message) return false;
      if (!isMyAdminMessage(message)) return false;
      if (!UNSENDABLE_FORMATS.has(message.message_format)) return false;
      return isWithinHoursFromNow(message.created_at, 1);
    },
    [isReadOnly, isMyAdminMessage],
  );

  const canAnnouncementMessage = useCallback((message) => {
    if (!message) return false;
    return UNSENDABLE_FORMATS.has(message.message_format);
  }, []);

  const canReactMessage = useCallback((message) => {
    if (!message || !supportsChatMessageReaction(message)) return false;
    return !isMyAdminMessage(message);
  }, [isMyAdminMessage]);

  const canReplyMessage = useCallback((message) => {
    if (!message) return false;
    const type = String(message.message_type || '').trim();
    const format = String(message.message_format || '').trim().toLowerCase();
    return ['admin_message', 'artist_message'].includes(type) && ['text', 'image', 'file'].includes(format);
  }, []);

  const canCopyMessage = useCallback((message) => hasCopyableChatMessageText(message), []);

  const canInspectReadStatus = useCallback((message) => {
    if (!message) return false;
    return Boolean(String(message.id || '').trim());
  }, []);

  const canOpenMessageMenu = useCallback(
    (message) =>
      canCopyMessage(message) ||
      canReplyMessage(message) ||
      canInspectReadStatus(message) ||
      canUnsendMessage(message) ||
      canAnnouncementMessage(message) ||
      canReactMessage(message),
    [canAnnouncementMessage, canCopyMessage, canInspectReadStatus, canReactMessage, canReplyMessage, canUnsendMessage],
  );

  const { menuRef: messageMenuRef, resolveMenuPosition } = useViewportBoundedMenuPosition({
    menu: messageMenu,
    setMenu: setMessageMenu,
    padding: 12,
    fallbackWidth: 248,
    fallbackHeight: 400,
  });

  const closeMessageMenu = useCallback(() => {
    setMessageMenu(null);
  }, []);

  const closePartialCopyMode = useCallback(() => {
    setInlineCopyMessageId('');
  }, []);

  const openMessageMenuAt = useCallback(
    (message, x, y) => {
      if (!message) return;
      const pos = resolveMenuPosition(x, y);
      setMessageMenu({ message, x: pos.x, y: pos.y });
    },
    [resolveMenuPosition],
  );

  const clearMessageLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleMessageContextMenu = useCallback(
    (event, message) => {
      if (!canOpenMessageMenu(message)) return;
      event.preventDefault();
      openMessageMenuAt(message, event.clientX, event.clientY);
    },
    [canOpenMessageMenu, openMessageMenuAt],
  );

  const handleMessageTouchStart = useCallback(
    (event, message) => {
      if (!isNarrow || !canOpenMessageMenu(message)) return;
      clearMessageLongPress();
      const touch = event.touches?.[0];
      if (!touch) return;
      const { clientX, clientY } = touch;
      longPressTimerRef.current = setTimeout(() => {
        openMessageMenuAt(message, clientX, clientY);
      }, 550);
    },
    [isNarrow, clearMessageLongPress, openMessageMenuAt, canOpenMessageMenu],
  );

  const handleUnsendMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target || !selectedEventId) return;
    closeMessageMenu();
    try {
      const data = await deleteEventChatMessage(selectedEventId, target.id);
      const deletedIds = Array.isArray(data?.messageIds) && data.messageIds.length
        ? data.messageIds.map((id) => String(id))
        : [String(target.id)];
      setMessages((prev) => removeMessages(prev, deletedIds));
    } catch (err) {
      if (toastError) toastError(err?.message || '送信取り消しに失敗しました。');
    }
  }, [messageMenu, closeMessageMenu, selectedEventId, toastError]);

  const handleToggleAnnouncementMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    closeMessageMenu();
    try {
      if (isAnnouncedMessage(target.id)) {
        await unpinAnnouncement(target.id);
      } else {
        await pinAnnouncement(target.id);
      }
    } catch (_) {}
  }, [closeMessageMenu, isAnnouncedMessage, messageMenu, pinAnnouncement, unpinAnnouncement]);

  const handleMessageMenuReactionSelect = useCallback(async (messageId, reactionType) => {
    closeMessageMenu();
    try {
      await toggleReaction(messageId, reactionType);
    } catch (_) {}
  }, [closeMessageMenu, toggleReaction]);

  const handleCopyMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    closeMessageMenu();
    if (!copyText) {
      toastError?.('コピーできる本文がありません。');
      return;
    }
    try {
      await writeTextToClipboard(copyText);
      success?.('メッセージをコピーしました。');
    } catch (error) {
      toastError?.(error?.message || 'コピーに失敗しました。');
    }
  }, [closeMessageMenu, messageMenu, success, toastError]);

  const handleOpenInlinePartialCopy = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    closeMessageMenu();
    if (!copyText) {
      toastError?.('コピーできる本文がありません。');
      return;
    }
    const nextMessageId = String(target.id || '').trim();
    setInlineCopyMessageId((current) => (current === nextMessageId ? '' : nextMessageId));
  }, [closeMessageMenu, messageMenu, toastError]);

  const handleCopySelectionText = useCallback(async (selectedText) => {
    if (!String(selectedText || '').trim()) {
      toastError?.('コピーしたい範囲を選択してください。');
      return;
    }
    try {
      await writeTextToClipboard(selectedText);
      success?.('選択範囲をコピーしました。');
    } catch (error) {
      toastError?.(error?.message || 'コピーに失敗しました。');
    }
  }, [success, toastError]);

  const renderPartialCopyContent = useCallback((message, contentNode, isSelfMessage = false) => {
    const normalizedMessageId = String(message?.id || '').trim();
    if (!normalizedMessageId || !canCopyMessage(message)) return contentNode;
    return (
      <ChatMessageSelectableCopyArea
        active={inlineCopyMessageId === normalizedMessageId}
        messageId={normalizedMessageId}
        copyButtonSide={isSelfMessage ? 'left' : 'right'}
        onDeactivate={closePartialCopyMode}
        onCopySelection={handleCopySelectionText}
      >
        {contentNode}
      </ChatMessageSelectableCopyArea>
    );
  }, [canCopyMessage, closePartialCopyMode, handleCopySelectionText, inlineCopyMessageId]);

  const buildReplyDraftFromMessage = useCallback((message) => {
    if (!message) return null;
    const format = String(message.message_format || '').trim().toLowerCase();
    return {
      messageId: String(message.id || '').trim(),
      senderName:
        message.message_type === 'admin_message'
          ? String(message.admin_display_name || message.admin_full_name || '管理者').trim()
          : String(message.artist_name || 'アーティスト').trim(),
      type: format,
      previewText: format === 'text' ? String(message.content || '').trim() : '',
      fileName: format === 'file' ? String(message.original_name || message.file_file_name || '').trim() : '',
    };
  }, []);

  const handleSelectReplyMessage = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    closeMessageMenu();
    setReplyDraft(buildReplyDraftFromMessage(target));
  }, [buildReplyDraftFromMessage, closeMessageMenu, messageMenu]);

  const handleOpenReadStatusDetail = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target?.id) return;
    closeMessageMenu();
    await openReadStatusDetail(target.id);
  }, [closeMessageMenu, messageMenu, openReadStatusDetail]);

  const handleSelectAnnouncementMessage = async (messageId) => {
    const normalizedMessageId = String(messageId || '').trim();
    if (!selectedEventId || !normalizedMessageId) return;
    setIsAnnouncementListOpen(false);
    await fetchMessages(selectedEventId, true, 0, { anchorMessageId: normalizedMessageId });
  };

  const isViewingLatest = useCallback(() => {
    const c = chatContainerRef.current;
    if (!c) return true;
    const nearBottomNormal = (c.scrollHeight - c.clientHeight - c.scrollTop) <= 80;
    const nearBottomReverse = Math.abs(c.scrollTop) <= 80;
    return nearBottomNormal || nearBottomReverse;
  }, []);
  const isNearTopForPaging = useCallback(() => {
    const c = chatContainerRef.current;
    if (!c) return false;
    const dir = (getComputedStyle(c).flexDirection || '').toLowerCase();
    const THRESH = 80;
    if (dir.includes('column-reverse')) {
      const maxAbs = c.scrollHeight - c.clientHeight;
      const absTop = Math.abs(c.scrollTop);
      return absTop >= (maxAbs - THRESH);
    } else {
      return c.scrollTop <= THRESH;
    }
  }, []);

  const formatEventListTimestamp = useCallback((dbString) => sidebarTimeLabelJST(dbString), []);

  const postEventReadReceipt = useCallback(async (eventId, lastReadMessageId) => {
    if (!eventId || !lastReadMessageId || !loggedInAdminId) return;
    const requestKey = `${eventId}:${lastReadMessageId}:${loggedInAdminId}`;
    if (pendingReadReceiptKeysRef.current.has(requestKey)) return;
    if (completedReadReceiptKeysRef.current.has(requestKey)) return;
    pendingReadReceiptKeysRef.current.add(requestKey);
    try {
      await markEventChatMessagesRead({
        eventId,
        lastReadMessageId,
        userType: 'admin',
        userId: loggedInAdminId,
      });
      completedReadReceiptKeysRef.current.add(requestKey);
      if (completedReadReceiptKeysRef.current.size > 500) {
        const firstKey = completedReadReceiptKeysRef.current.values().next().value;
        if (firstKey) completedReadReceiptKeysRef.current.delete(firstKey);
      }
      refreshEventChatUnread();
    } catch {}
    finally {
      pendingReadReceiptKeysRef.current.delete(requestKey);
    }
  }, [loggedInAdminId, refreshEventChatUnread]);

  /* 既読処理 */
  const markLatestRead = useCallback(() => {
    if (!isEventChatAreaOpenRef.current) return;
    const currentEventId = selectedEventIdRef.current;
    const currentMessages = messagesRef.current;
    if (!currentEventId || currentMessages.length === 0) return;
    const newest = currentMessages[0]; // state は DESC(index0=最新)
    selfReadGuardRef.current = Date.now();
    postEventReadReceipt(currentEventId, newest.id).catch(() => {});
    markEventAsRead(currentEventId);
  }, [markEventAsRead, postEventReadReceipt]);

  /* 新着バナー */
  const openBanner = useCallback((payload) => {
    if (payload?.id && lastBannerIdRef.current === payload.id) return;
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
      bannerTimeoutRef.current = null;
    }
    lastBannerIdRef.current = payload?.id ?? null;
    setBannerMsg(payload);
    setShowNewMsgBanner(true);
    bannerTimeoutRef.current = setTimeout(() => {
      setShowNewMsgBanner(false);
      lastBannerIdRef.current = null;
      bannerTimeoutRef.current = null;
    }, 3000);
  }, []);
  const handleBannerClick = async () => {
    setShowNewMsgBanner(false);
    try {
      if (selectedEventIdRef.current) {
        setActiveAnchorMessageId('');
        setHasNewerMessages(false);
        await fetchMessages(selectedEventIdRef.current, true, 0, { anchorMessageId: '' });
      }
      const c = chatContainerRef.current;
      if (c) c.scrollTop = c.scrollHeight;
      const currentMessages = messagesRef.current;
      if (!(currentMessages.length && isMyAdminMessage(currentMessages[0]))) {
        markLatestRead();
      }
    } catch (error) {
      dlog('handleBannerClick failed:', error);
    }
  };

  /* イベント詳細キャッシュ */
  const getEventDetail = useCallback((async (uuid) => {
    if (!uuid) return null;
    if (eventDetailCacheRef.current.has(uuid)) return eventDetailCacheRef.current.get(uuid);
    if (eventDetailInFlightRef.current.has(uuid)) return await eventDetailInFlightRef.current.get(uuid);

    const p = getEventDetailFromApi(uuid)
      .catch(() => null)
      .finally(() => eventDetailInFlightRef.current.delete(uuid));

    eventDetailInFlightRef.current.set(uuid, p);
    const d = await p;
    if (d) eventDetailCacheRef.current.set(uuid, d);
    return d;
  }), []);
  const fetchEventDetail = useCallback(async (eventId) => {
    try {
      const d = await getEventDetail(eventId);
      if (!d) return;
      const listRow = events.find((evt) => evt?.event_uuid === eventId) || null;
      const sidebarRow = sidebarDetails?.event_uuid === eventId ? sidebarDetails : null;
      let merged = { ...d };
      if (d.performance_flag === 'additional' && d.use_existing_flyers && d.original_event_uuid) {
        const o = await getEventDetail(d.original_event_uuid);
        if (o) {
          merged.flyer_front_url = o.flyer_front_url || merged.flyer_front_url;
          merged.flyer_back_url = o.flyer_back_url || merged.flyer_back_url;
        }
      }
      merged = {
        ...(listRow || {}),
        ...(sidebarRow || {}),
        ...merged,
        chat_thread_id:
          resolveEventThreadId(merged) ||
          resolveEventThreadId(sidebarRow) ||
          resolveEventThreadId(listRow) ||
          null,
        chat_mode:
          resolveExplicitEventChatMode(merged || {}) ??
          resolveExplicitEventChatMode(sidebarRow || {}) ??
          resolveExplicitEventChatMode(listRow || {}) ??
          'separate',
        group_event_count:
          resolveExplicitEventGroupCount(merged || {}) ??
          resolveExplicitEventGroupCount(sidebarRow || {}) ??
          resolveExplicitEventGroupCount(listRow || {}) ??
          1,
      };
      merged.performance_type = normalizeEventPerformanceTypeForThread(
        merged,
        pickFirstString(
          d?.performance_type,
          sidebarRow?.performance_type,
          listRow?.performance_type
        )
      );
      setSelectedEventInfo(merged);
      setSidebarDetails((prev) => ({ ...(prev || {}), ...merged }));
      dlog('fetchEventDetail ->', eventId, 'detail:', merged);
    } catch {}
  }, [events, getEventDetail, sidebarDetails]);

  /* サイドバー行再取得 */
  const fetchSidebarRow = useCallback(async (eventId) => {
    const rawFallbackDetail = await getEventDetail(eventId);
    let fallbackDetail = rawFallbackDetail;
    if (
      rawFallbackDetail?.performance_flag === 'additional' &&
      rawFallbackDetail?.use_existing_flyers &&
      rawFallbackDetail?.original_event_uuid
    ) {
      const original = await getEventDetail(rawFallbackDetail.original_event_uuid);
      if (original) {
        fallbackDetail = {
          ...rawFallbackDetail,
          flyer_front_url: rawFallbackDetail.flyer_front_url || original.flyer_front_url || null,
          flyer_back_url: rawFallbackDetail.flyer_back_url || original.flyer_back_url || null,
        };
      }
    }
    const normalize = (row, base) => {
      const mergedRow = {
        ...(row || {}),
      event_uuid: String(row?.event_uuid || base?.event_uuid || eventId || ''),
      name: pickFirstString(row?.name, base?.name),
      event_date: row?.event_date || base?.event_date || null,
      open_time: row?.open_time || base?.open_time || null,
      start_time: row?.start_time || base?.start_time || null,
      venue_name: pickFirstString(row?.venue_name, row?.venue, row?.place, base?.venue_name, base?.venue, base?.place),
      venue: pickFirstString(row?.venue, row?.place, base?.venue, base?.place),
      prefecture_name: pickFirstString(row?.prefecture_name, row?.prefecture, base?.prefecture_name, base?.prefecture),
      city_name: pickFirstString(row?.city_name, row?.city, base?.city_name, base?.city),
      venue_address: pickFirstString(row?.venue_address, row?.address, base?.venue_address, base?.address),
      memo: pickFirstString(row?.memo, row?.note, row?.remarks, base?.memo, base?.note, base?.remarks),
      flyer_front_url: pickFirstString(row?.flyer_front_url, base?.flyer_front_url),
      flyer_back_url: pickFirstString(row?.flyer_back_url, base?.flyer_back_url),
      performance_flag: row?.performance_flag ?? base?.performance_flag ?? null,
      use_existing_flyers: row?.use_existing_flyers ?? base?.use_existing_flyers ?? false,
      original_event_uuid: row?.original_event_uuid ?? base?.original_event_uuid ?? null,
      };
      mergedRow.performance_type = normalizeEventPerformanceTypeForThread(
        mergedRow,
        pickFirstString(row?.performance_type, base?.performance_type)
      );
      return mergedRow;
    };
    try {
      const rowPayload = await getAdminEventChatSidebarRow(eventId);
      const row = rowPayload?.event || null;
      if (row || fallbackDetail) return normalize(row, fallbackDetail);
      return null;
    } catch {
      if (fallbackDetail) return normalize(null, fallbackDetail);
      return null;
    }
  }, [getEventDetail]);
  const fetchRepresentativeEventIdByThread = useCallback(async (threadId) => {
    const normalizedThreadId = String(threadId || '').trim();
    if (!normalizedThreadId) return null;
    try {
      const payload = await fetchJsonSafe(
        ADMIN_REPRESENTATIVE_EVENT_BY_THREAD_URL(normalizedThreadId),
        {},
        { label: 'スレッド情報の取得に失敗しました。' }
      );
      return payload?.event_uuid || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!selectedEventId) {
      setSidebarDetails(null);
      setIsSidebarLoading(false);
      return undefined;
    }
    setIsSidebarLoading(true);
    setSidebarDetails(null);
    fetchSidebarRow(selectedEventId)
      .then((row) => {
        if (cancelled) return;
        setSidebarDetails(row);
      })
      .catch(() => {
        if (cancelled) return;
        setSidebarDetails(null);
      })
      .finally(() => {
        if (!cancelled) setIsSidebarLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedEventId, fetchSidebarRow]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedEventId || !isSelectedGroupUnified) {
      setGroupPanelEvents([]);
      return undefined;
    }
    (async () => {
      try {
        const payload = await fetchJsonSafe(ADMIN_GROUP_EVENTS_URL(selectedEventId), {}, { label: '' });
        if (cancelled) return;
        setGroupPanelEvents(normalizeGroupEventRows(payload?.events));
      } catch {
        if (!cancelled) setGroupPanelEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isSelectedGroupUnified, selectedEventId]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedEventId || !isSelectedGroupUnified || effectiveGroupEvents.length <= 0) {
      setGroupEventSidebarRows([]);
      return undefined;
    }
    (async () => {
      const rows = await Promise.all(
        effectiveGroupEvents.map(async (base) => {
          const eventUuid = String(base?.event_uuid || '').trim();
          if (!eventUuid) return null;
          try {
            const detail = await fetchSidebarRow(eventUuid);
            return mergeEventSidebarRow({
              detail,
              base,
              eventUuid,
              pickFirstStringFn: pickFirstString,
            });
          } catch {
            return mergeEventSidebarRow({
              detail: null,
              base,
              eventUuid,
              pickFirstStringFn: pickFirstString,
            });
          }
        })
      );
      if (cancelled) return;
      setGroupEventSidebarRows(rows.filter(Boolean));
    })();
    return () => { cancelled = true; };
  }, [effectiveGroupEvents, fetchSidebarRow, isSelectedGroupUnified, selectedEventId]);

  useEffect(() => {
    let cancelled = false;
    if (!isSelectedGroupUnified || effectiveGroupEvents.length <= 0) {
      setGroupCastingSummaryMap({});
      setGroupTaskSummaryMap({});
      setGroupTicketSummaryMap({});
      return undefined;
    }

    const groupEventIds = Array.from(
      new Set(
        effectiveGroupEvents
          .map((event) => String(event?.event_uuid || '').trim())
          .filter(Boolean)
      )
    );
    if (groupEventIds.length <= 0) {
      setGroupCastingSummaryMap({});
      setGroupTaskSummaryMap({});
      setGroupTicketSummaryMap({});
      return undefined;
    }

    (async () => {
      if (canViewCastingProgress) {
        try {
          const summaries = await getCastingSummaries(groupEventIds);
          if (!cancelled) setGroupCastingSummaryMap(summaries);
        } catch {
          if (!cancelled) setGroupCastingSummaryMap({});
        }
      } else if (!cancelled) {
        setGroupCastingSummaryMap({});
      }

      if (canViewEventTasks) {
        try {
          const summaries = await getEventTaskSummaries({ eventUuids: groupEventIds });
          if (!cancelled) setGroupTaskSummaryMap(summaries);
        } catch {
          if (!cancelled) setGroupTaskSummaryMap({});
        }
      } else if (!cancelled) {
        setGroupTaskSummaryMap({});
      }

      if (canViewTicketSales) {
        try {
          const results = await Promise.all(
            groupEventIds.map(async (eventUuid) => {
              try {
                const summary = await getEventTicketSalesSummary(eventUuid);
                return [eventUuid, summary];
              } catch {
                return [eventUuid, null];
              }
            })
          );
          if (!cancelled) {
            setGroupTicketSummaryMap(results.reduce((acc, [eventUuid, summary]) => {
              acc[eventUuid] = summary;
              return acc;
            }, {}));
          }
        } catch {
          if (!cancelled) setGroupTicketSummaryMap({});
        }
      } else if (!cancelled) {
        setGroupTicketSummaryMap({});
      }
    })();

    return () => { cancelled = true; };
  }, [canViewCastingProgress, canViewEventTasks, canViewTicketSales, effectiveGroupEvents, isSelectedGroupUnified]);

  /* 初回ブート */
  const [isBootLoading, setIsBootLoading] = useState(true);
  const upsertEventRow = useCallback((row, { moveToTop = true } = {}) => {
    if (!row?.event_uuid) return;
    joinEventRooms({ socket, joinedRoomsRef, events: [row] });
    setEvents((prev) => {
      const previousRow =
        prev.find((eventRow) => eventRow && eventRow.event_uuid === row.event_uuid) || null;
      const patched = buildRealtimeEventSidebarPatch({
        row,
        previousRow,
        nowJstDbString,
        resolveEventThreadId,
        resolveEventChatMode,
        resolveEventGroupCount,
        includeAssignedAdmins: true,
        includeUnreadCountFromRaw: true,
      });
      if (shouldSuppressEventUnread(row.event_uuid)) {
        patched.unreadCount = 0;
        patched.unread_count = 0;
      }
      return upsertEventSidebarRowByUuid({
        prevEvents: prev,
        eventUuid: row.event_uuid,
        patch: patched,
        moveToTop,
        orderFrozen,
      });
    });
  }, [orderFrozen, shouldSuppressEventUnread]);

  const fetchEvents = useCallback(async (mode = 'reset', { retried = false } = {}) => {
    if (checking) return 0;
    if (!canUseEventChat) {
      setEvents([]);
      setEventsOffset(0);
      setHasMoreEvents(false);
      setEventChatMessageCount(0);
      return 0;
    }
    const offsetForFetch = mode === 'append' ? eventsOffset : 0;
    try {
      setIsLoadingEvents(true);
      if (mode !== 'append') {
        eventListForceFallbackExhaustedRef.current = false;
      }

      const {
        events: rawEvents = [],
        forbidden = false,
      } = await loadEventListRows({
        mode,
        offset: offsetForFetch,
        headUrl: ADMIN_EVENTS_HEAD_URL,
        pageUrlBuilder: ADMIN_EVENTS_PAGE_URL,
        headModes: ['reset', 'refreshTop'],
        fetchJsonSafe,
        errorLabel: 'イベント一覧の取得に失敗しました。',
      });

      if (forbidden) {
        const refreshed = await fetchSession?.().catch(() => null);
        const stillAllowed = canUseEventChatForSession(refreshed);
        if (stillAllowed && !retried) {
          await wait(300);
          return fetchEvents(mode, { retried: true });
        }
        if (!stillAllowed) {
          setEvents([]);
          setEventsOffset(0);
          setHasMoreEvents(false);
          setEventChatMessageCount(0);
        } else if (!eventListRetryTimerRef.current) {
          eventListRetryTimerRef.current = window.setTimeout(() => {
            eventListRetryTimerRef.current = null;
            fetchEvents(mode, { retried: false });
          }, 1200);
        }
        return 0;
      }

      const updatedEvents = await Promise.all(
        rawEvents.map(async evt => {
          try {
            const detail = await getEventDetail(evt.event_uuid);
            if (!detail) return composeAdminEventRowWithDetail({ eventRow: evt, detail: null });
            let originalDetail = null;
            if (
              detail.performance_flag === 'additional' &&
              detail.use_existing_flyers &&
              detail.original_event_uuid
            ) {
              originalDetail = await getEventDetail(detail.original_event_uuid);
            }
            return composeAdminEventRowWithDetail({
              eventRow: evt,
              detail,
              originalDetail,
            });
          } catch {
            return composeAdminEventRowWithDetail({ eventRow: evt, detail: null });
          }
        })
      );

      const withUnread = updatedEvents.map((evt) => {
        const nextUnreadCount = shouldSuppressEventUnread(evt?.event_uuid)
          ? 0
          : Number(evt?.unread_count || 0);
        return {
          ...evt,
          unreadCount: nextUnreadCount,
          unread_count: nextUnreadCount,
        };
      });

      const modeState = resolveEventListModeState({
        mode,
        prevEvents: events,
        incomingEvents: withUnread,
        selectedEventId,
        pageLimit: EVENTS_PAGE_LIMIT,
        refreshTopSliceToLimit: false,
        appendOffsetSource: 'incoming',
        appendHasMoreSource: 'incoming',
        appendHasMoreComparator: 'gte',
        appendJoinSource: 'added',
      });
      joinEventRooms({ socket, joinedRoomsRef, events: modeState.joinEvents });
      setEvents(modeState.nextEvents);
      if (modeState.offsetMode === 'set') {
        setEventsOffset(modeState.offsetValue);
      } else if (modeState.offsetMode === 'increment') {
        setEventsOffset((prev) => prev + modeState.offsetValue);
      }
      setHasMoreEvents(modeState.hasMoreEvents);

      dlog('fetchEvents', { mode, count: rawEvents.length });
      refreshEventChatUnread();
      return rawEvents.length;
    } catch (err) {
      dlog('fetchEvents error', err);
      return 0;
    } finally {
      setIsLoadingEvents(false);
    }
  }, [
    canUseEventChat,
    canUseEventChatForSession,
    checking,
    events,
    eventsOffset,
    fetchSession,
    getEventDetail,
    refreshEventChatUnread,
    selectedEventId,
    setEventChatMessageCount,
    shouldSuppressEventUnread,
  ]);

  useEffect(() => {
    if (typeof setAdminEventChatTone !== 'function') return;
    const unreadEventIds =
      eventUnreadMapAdmin instanceof Map
        ? Array.from(eventUnreadMapAdmin.keys())
        : Object.keys(eventUnreadMapAdmin || {});
    const hasAllUnreadEvents =
      unreadEventIds.length === 0 ||
      unreadEventIds.every((eventId) =>
        events.some((evt) => String(evt?.event_uuid ?? evt?.event_id ?? evt?.id) === String(eventId))
      );
    const unreadEvents = events.filter((evt) => {
      if (!evt) return false;
      let unread = resolveEventUnread(evt);
      if (evt.event_uuid === selectedEventId) unread = 0;
      return unread > 0;
    });
    if (!unreadEvents.length) {
      if (unreadEventIds.length > 0) return;
      setAdminEventChatTone(null);
      return;
    }
    if (!hasAllUnreadEvents) return;
    const hasMissingAssignment = unreadEvents.some((evt) => {
      const listAssigned = Array.isArray(evt?.assigned_admins) && evt.assigned_admins.length > 0;
      const cached = eventDetailCacheRef.current.get(evt?.event_uuid);
      const cachedAssigned = Array.isArray(cached?.assigned_admins) && cached.assigned_admins.length > 0;
      return !(listAssigned || cachedAssigned);
    });
    if (hasMissingAssignment && adminEventChatTone) return;
    const hasAlert = unreadEvents.some((evt) => isAdminAssignedToEvent(evt));
    setAdminEventChatTone(hasAlert ? 'alert' : 'ok');
  }, [
    events,
    eventUnreadMapAdmin,
    resolveEventUnread,
    selectedEventId,
    adminEventChatTone,
    setAdminEventChatTone,
  ]);

  const {
    searchSummaryLabel,
    assigneeQuickToggles,
    displayedEvents,
    openTargetSearchModal,
    clearSearch,
    searchToolbarChips,
    applyTargetSearchModal,
    requestRefreshTop,
    isTargetFilterMode,
    handleEventListScroll,
  } = useAdminEventChatListState({
    ADMIN_SEARCH_API,
    API_URL,
    EVENTS_PAGE_LIMIT,
    EVENT_ASSIGNEE_SEARCH_STORAGE_KEY,
    applyEventTargetSearchDraft,
    applySearchResultEvents,
    assigneeSearch,
    buildToolbarFilterChips,
    clearEventTargetSearch,
    composeAdminEventRowWithDetail,
    countActiveEventSearchFilters,
    dlog,
    eventListForceFallbackExhaustedRef,
    eventListLoadMorePendingRef,
    eventListNode,
    events,
    fetchEvents,
    fetchJsonSafe,
    formatSearchChipDate,
    getEventDetail,
    hasMoreEvents,
    info,
    isAdminAssignedToEvent,
    isBootLoading,
    isLoadingEvents,
    isSearchLoadingMore,
    isSearchMode,
    isSearching,
    loadEventSearchRowsWithUnread,
    loadMoreEventList,
    loadMoreNode,
    messageSearchQuery,
    observeEventListLoadMoreScroll,
    observeEventListLoadMoreSentinel,
    openEventTargetSearchModal,
    orderFrozen,
    refreshEventChatUnread,
    refreshTopTimerRef,
    resolveEventSearchCriteria,
    scheduleEventListRefreshTop,
    searchDraft,
    searchEndDate,
    searchHasMore,
    searchNextOffset,
    searchQuery,
    searchSnapshotToken,
    searchStartDate,
    setAssigneeSearch,
    setEvents,
    setEventsOffset,
    setHasMoreEvents,
    setIsSearchLoadingMore,
    setIsSearchMode,
    setIsSearching,
    setMessageSearchQuery,
    setSearchDraft,
    setSearchEndDate,
    setSearchHasMore,
    setSearchModalError,
    setSearchNextOffset,
    setSearchQuery,
    setSearchSnapshotToken,
    setSearchStartDate,
    setShowSearchModal,
    shouldLoadMoreEventList,
    toastError,
    triggerEventListLoadMoreIfNeeded,
  });

  /* メッセージ取得系 */
  const {
    buildRealtimeEventTextMessage,
    fetchMessages,
    fetchNewMessages,
    loadNewerMessages,
    reconcileRecentMessages,
    bumpEventToTop,
    updateSidebarAfterSend,
    clearLast,
    saveLast,
    handleSelectEvent,
    openAiInbox,
    handleOpenAiTask,
    handleDownload,
    imageItems,
    displayMessages,
    shouldShowChatGuidance,
    openImageModalByIdOrSrc,
    closeImgModal,
    handleJumpToReplyMessage,
    showPrevImg,
    showNextImg,
  } = useAdminEventChatMessages({
    ADMIN_MESSAGE_FORMATS,
    API_URL,
    CHAT_FOLLOWUP_AI_TARGET_ID,
    EARLIEST_CREATED_AT,
    RECON_LIMIT,
    anchorHighlightTimerRef,
    bannerTimeoutRef,
    buildImageGroupDisplayMessages,
    buildMessageImageUrl,
    chatContainerRef,
    currentRoomRef,
    dlog,
    fetchEventDetail,
    fetchRepresentativeEventIdByThread,
    fetchMessagesRef,
    fetchNewMessagesRef,
    focusChatAnchorMessage,
    getEventChatMessagesAround,
    hasMore,
    hasNewerMessages,
    info,
    initialLoadRef,
    isAiTargetSelected,
    isEventChatAreaOpenRef,
    isFetching,
    isInitialLoading,
    isNarrow,
    isSearchMode,
    lastBannerIdRef,
    lastDiffFetchRef,
    latestCreatedAtRef,
    limit,
    listEventChatMessages,
    listEventChatNewMessages,
    loadedMessagesEventId,
    loadingNewerMessages,
    loadingOlderMessages,
    loggedInAdminId,
    markEventUnreadLocally,
    markLatestRead,
    mergeMessages,
    messageIdsRef,
    messageWindowOffset,
    messages,
    mobileEventChatHistoryRef,
    mobileEventListPrefRef,
    mobileView,
    navigate,
    nowJstDbString,
    oldestCreatedAtRef,
    openExternalUrl,
    orderFrozen,
    patchMessages,
    pendingRestoreRef,
    pickFirstString,
    pickRealtimeReadCount,
    postEventReadReceipt,
    prevHistoryForChatImageRef,
    pushViewState,
    replaceViewState,
    requestRefreshTop,
    selectedEventId,
    selectedEventIdRef,
    setActiveAnchorMessageId,
    setEvents,
    setHasMore,
    setHasNewerMessages,
    setFollowupRefreshKey,
    setImgModalIndex,
    setImgModalOpen,
    setIsAiTargetSelected,
    setIsFetching,
    setIsInitialLoading,
    setLatestCreatedAt,
    setLoadedMessagesEventId,
    setLoadingNewerMessages,
    setLoadingOlderMessages,
    setMessageWindowOffset,
    setMessages,
    setMobileView,
    setOffset,
    setSelectedEventId,
    setSelectedEventInfo,
    setSettingsEvent,
    setShowNewMsgBanner,
    setShowNotes,
    setShowSettingsModal,
    settingsEventIdRef,
    settingsEventRef,
    socket,
    toastError,
  });

  const announcementChecks = buildEventAnnouncementChecks({
    isNoteCreateMessage,
    isNoteUpdateMessage,
    isNoteDeleteMessage,
    isTimetableMemoMessage,
    isEventInfoPublishedMessage,
    isEventMembersPublishedMessage,
    isStageSeatingSharedMessage,
    isEventInfoUpdatedMessage,
    isEventFinishedMessage,
    isEventMaterialSharedMessage,
    isEventImagesAddedMessage,
    isTimetableCreatedMessage,
    isTimetableUpdatedMessage,
    isTimetableRuntimeMessage,
    isNoteMessage,
  });

  const resolveMessageProxySource = (msg, fullSrc) => {
    const idx = imageItems.findIndex(
      (it) =>
        String(it.id) === String(msg.image_id) ||
        it.fullSrc === fullSrc ||
        it.proxySrc === msg.image_url
    );
    return (idx >= 0 ? imageItems[idx].proxySrc : null)
      || buildMessageImageUrl(msg.image_file_name, 'proxy')
      || fullSrc;
  };

  const buildNoteActionNode = (msg) => {
    if (!canViewEventNotes) return null;
    const messageNoteId = msg.note_id || null;
    return (
      <EventNoteActionButton
        messageNoteId={messageNoteId}
        onOpen={() => {
          setShowNotes(true);
          pushViewState(selectedEventId, 'notes-list');
          if (messageNoteId) {
            window.setTimeout(() => {
              window.dispatchEvent(new CustomEvent('admin-notes-open-detail', { detail: { noteId: messageNoteId } }));
            }, 0);
          }
        }}
      />
    );
  };

  const buildAlbumActionNode = () => {
    if (!canViewAlbum && !canEditAlbum) return null;
    return (
      <button
        type="button"
        className="system-message-card__link"
        onClick={() => setShowAlbumModal(true)}
      >
        アルバムを見る
      </button>
    );
  };

  const buildMaterialActionNode = () => (
    <button
      type="button"
      className="system-message-card__link"
      onClick={() => setShowMaterialsModal(true)}
    >
      資料を見る
    </button>
  );

  const buildEventFinishedActionNode = (msg = {}) => {
    const eventId = String(
      msg.source_event_id ||
      msg.sourceEventId ||
      msg.event_uuid ||
      msg.eventUuid ||
      msg.event_id ||
      msg.eventId ||
      selectedEventId ||
      '',
    ).trim();
    const contractArtistId = String(
      msg.event_finished_contract_artist_id ||
      msg.contract_artist_id ||
      msg.contractArtistId ||
      '',
    ).trim();
    const href = eventId
      ? `/admin/workspaces/contracts/history?event_uuid=${encodeURIComponent(eventId)}`
      : contractArtistId
        ? `/admin/workspaces/contracts/${encodeURIComponent(contractArtistId)}`
        : '';
    if (!href) return null;
    return (
      <a className="system-message-card__link" href={href}>
        {eventId ? '本契約リストを見る' : '契約詳細を開く'}
      </a>
    );
  };

  const linkifyText = useCallback(
    (text, options = {}) =>
      linkifyTextNodes(text, {
        ...options,
        transformUrl: normalizeStageSeatingShareUrlForAdmin,
      }),
    [],
  );

  const {
    withAnnouncementScopeTitle,
    getAnnouncementToneClass,
    renderMessageContent,
  } = createEventMessageRenderPipeline(createEventPipelineOptions({
    selectedEventInfo,
    selectedEventId,
    resolveEventChatMode,
    unifiedMode: CHAT_MODE_GROUP_UNIFIED,
    announcementChecks,
    getAnnouncementIconClass,
    linkifyText,
    parseTimetableRuntimePayload: parseEventTimetableRuntimePayload,
    isTimetableMemoMessage,
    resolveTimetableRuntimeVisibility: resolveEventTimetableRuntimeVisibility,
    buildTimetableDetailUrl: ({ selectedEventId: eventId, timetableId }) =>
      eventId && timetableId != null
        ? `/admin/workspaces/event-operations/events/${encodeURIComponent(eventId)}/timetables/${encodeURIComponent(timetableId)}`
        : '',
    messageFormats: ADMIN_MESSAGE_FORMATS,
    messageFilePath: MESSAGE_FILE_PATH,
    isTimetableRuntimeMessage,
    isSimpleAnnouncementMessage,
    isEventFinishedMessage,
    isEventMaterialSharedMessage,
    isEventImagesAddedMessage,
    isNoteMessage,
    isNoteDeleteMessage,
    buildNoteActionNode,
    buildMaterialActionNode,
    buildAlbumActionNode,
    buildEventFinishedActionNode,
    buildMessageImageUrl,
    onOpenImage: (msg, fullSrc) => openImageModalByIdOrSrc(msg.image_id, fullSrc),
    onDownload: handleDownload,
    buildAssetUrl,
    ellipsizeFileName,
    getFileIconClass,
    formatExpireDateLabel,
    onMentionClick: handleMentionClick,
    contentRendererOverrides: {
      resolveImageProxySource: resolveMessageProxySource,
    },
  }));

  /* 初回ブート（ヘッダーは常時表示、本文部は切替） */
  useEffect(() => {
    if (!adminSessionLoaded || checking) return;
    if (initialAppliedRef.current) return;
    initialAppliedRef.current = true;

    replaceViewState(null, 'chat');

    (async () => {
      setIsBootLoading(true);
      try {
        dlog('boot start', { initialEventId, initialView, locationState: location.state });

        await fetchEvents('reset');

        const fromSidebarReset = Boolean(location?.state?.reset);
        if (fromSidebarReset) {
          dlog('fromSidebarReset -> clear and suppress restore');
          clearLast();
          navigate('/admin/workspaces/event-operations/event-chat', { replace: true, state: null });
          setIsAiTargetSelected(true);
          mobileEventListPrefRef.current = isNarrow;
          mobileEventChatHistoryRef.current = false;
          if (isNarrow) {
            setMobileView('list');
            replaceViewState(null, 'list');
          } else {
            replaceViewState(null, 'chat');
          }
          pendingRestoreRef.current = false;
          return;
        }

        const hasInitialTarget = Boolean(initialThreadId || initialEventId);
        let initialTargetEventId = null;
        if (initialThreadId) {
          initialTargetEventId = await fetchRepresentativeEventIdByThread(initialThreadId);
          dlog('boot thread param resolve', { initialThreadId, initialTargetEventId });
        }
        if (!initialTargetEventId && initialEventId) {
          initialTargetEventId = initialEventId;
        }

        if (initialTargetEventId) {
          dlog('boot via query param', { initialThreadId, initialEventId, initialTargetEventId, initialView });
          try {
            let row = await fetchSidebarRow(initialTargetEventId);
            if (row) {
              if (
                row.performance_flag === 'additional' &&
                row.use_existing_flyers &&
                row.original_event_uuid &&
                !row.flyer_front_url
              ) {
                const o = await getEventDetail(row.original_event_uuid);
                if (o?.flyer_front_url) row.flyer_front_url = o.flyer_front_url;
              }
              upsertEventRow(row, { moveToTop: false });
            }
            handleSelectEvent(initialTargetEventId, {
              clearUrl: false,
              anchorMessageId: initialAnchorMessageId,
            });
            const v =
              canViewEventNotes && (initialView || '').toLowerCase() === 'notes' ? 'notes-list' : 'chat';
            setShowNotes(v === 'notes-list');
            pushViewState(initialTargetEventId, v);
            saveLast(initialTargetEventId, v);
            mobileEventListPrefRef.current = false;
            if (isNarrow) setMobileView('chat');
          } catch (err) {
            dlog('boot via query param error', err);
            mobileEventListPrefRef.current = isNarrow;
            mobileEventChatHistoryRef.current = false;
            if (isNarrow) setMobileView('list');
            pendingRestoreRef.current = false;
          }
        } else if (hasInitialTarget) {
          dlog('boot query param target not found', { initialThreadId, initialEventId });
          setIsAiTargetSelected(true);
          mobileEventListPrefRef.current = isNarrow;
          mobileEventChatHistoryRef.current = false;
          if (isNarrow) {
            setMobileView('list');
            replaceViewState(null, 'list');
          } else {
            replaceViewState(null, 'chat');
          }
          pendingRestoreRef.current = false;
        } else {
          dlog('boot default AI inbox');
          setIsAiTargetSelected(true);
          mobileEventListPrefRef.current = isNarrow;
          mobileEventChatHistoryRef.current = false;
          if (isNarrow) {
            setMobileView('list');
            replaceViewState(null, 'list');
          } else {
            replaceViewState(null, 'chat');
          }
          pendingRestoreRef.current = false;
        }
      } finally {
        setIsBootLoading(false);
        requestAnimationFrame(() => setOrderFrozen(false));
        dlog('boot end');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminSessionLoaded,
    checking,
    initialThreadId,
    initialEventId,
    initialView,
    initialAnchorMessageId,
    location.state,
    isNarrow,
  ]);

  /* サイドバーから「未選択で開く」指示を受けたとき */
  useEffect(() => {
    if (!location?.state?.reset || checking) return;

    dlog('sidebar reset while mounted -> unselect');

    clearLast();

    if (currentRoomRef.current) {
      try { socket.emit('leave_event', currentRoomRef.current); } catch {}
      currentRoomRef.current = null;
    }

    setSelectedEventId(null);
    setSelectedEventInfo(null);
    setMessages([]);
    messageIdsRef.current = new Set();
    setShowNotes(false);
    setShowSettingsModal(false);
    mobileEventListPrefRef.current = true;
    mobileEventChatHistoryRef.current = false;
    setShowNewMsgBanner(false);
    if (bannerTimeoutRef.current) { clearTimeout(bannerTimeoutRef.current); bannerTimeoutRef.current = null; }
    lastBannerIdRef.current = null;
    setOffset(0);
    setHasMore(true);
    setLatestCreatedAt(null);

    fetchEvents('reset');
    navigate('/admin/workspaces/event-operations/event-chat', { replace: true, state: null });

    // モバイルなら一覧全画面へ
    if (isNarrow) {
      setMobileView('list');
    }
    replaceViewState(null, isNarrow ? 'list' : 'chat');
  }, [checking, clearLast, fetchEvents, isNarrow, location.state, navigate]); // location.state のみ依存

  useEffect(() => () => {
    if (eventListRetryTimerRef.current) {
      clearTimeout(eventListRetryTimerRef.current);
      eventListRetryTimerRef.current = null;
    }
  }, []);
  const {
    shouldShowMessageSourceBadge,
    handleAvatarClick,
    renderAnnouncementAvatarLeft,
    renderAdminAvatarLeft,
    renderArtistAvatarLeft,
    openSettingsModal,
    closeSettingsModal,
    clearSidebarContextMenuBlock,
    clearSidebarLongPress,
    handleSidebarTouchStart,
    toggleMuteImmediately,
    promptSelfAssignmentAfterSend,
    isDragActive,
    overlayRect,
  } = useAdminEventChatRuntime({
    ADMIN_MESSAGE_FORMATS,
    ADMIN_PROFILE_IMAGE_PATH,
    ARTIST_PROFILE_IMAGE_PATH,
    CHAT_MODE_GROUP_UNIFIED,
    SOCKET_EVENTS,
    STALE_MS,
    adminLookup,
    appendVariant,
    bannerTimeoutRef,
    buildAdminProfilePayload,
    buildArtistProfilePayload,
    buildAssetUrl,
    buildParticipantEventMembershipLabel,
    buildParticipantEventPartSummary,
    buildRealtimeEventTextMessage,
    bumpEventToTop,
    chatContainerRef,
    cleanupTimeoutRefs,
    clearPendingEventRead,
    closeImgModal,
    confirmAssignmentForEvents,
    createEventAvatarProfileTargetResolver,
    createEventAvatarRenderers,
    effectiveGroupEvents,
    eventChatAreaRef,
    events,
    fetchMessages,
    fetchMessagesRef,
    fetchNewMessages,
    fetchNewMessagesRef,
    fetchSidebarRow,
    findPartLabelByIdRef,
    formatAdminDisplayName,
    getAdminEventChatNotifyMute,
    getAnnouncementIconClass,
    getAnnouncementToneClass,
    getEventDetail,
    handleSelectEvent,
    hasMore,
    hasNewerMessages,
    imgModalOpen,
    isAnnouncementMessage,
    isEventChatAreaOpenRef,
    isFetching,
    isMyAdminMessage,
    isNarrow,
    isNearTopForPaging,
    isNoteDeleteMessage,
    isNoteMessage,
    isNoteUpdateMessage,
    isSavingSettings,
    isSearchModeRef,
    isSelectedGroupUnified,
    isViewingLatest,
    lastDiffFetchRef,
    loadNewerMessages,
    loadingNewerMessages,
    loggedInAdminId,
    markEventUnreadLocally,
    markLatestRead,
    mergeMessages,
    messageIdsRef,
    messages,
    messagesRef,
    mobileEventChatHistoryRef,
    mobileEventListPrefRef,
    muteSetting,
    normalizeEventUuidList,
    nowJstDbString,
    offset,
    openBanner,
    openProfileTarget,
    orderFrozenRef,
    parsePartIds,
    participantArtistMap,
    participantSnapshot,
    patchMessageReadCountsByIds,
    pickFirstString,
    postEventReadReceipt,
    reconcileRecentMessages,
    reconcileTimerRef,
    refreshAnnouncements,
    refreshEventChatUnread,
    refreshMessageReactionDetails,
    refreshPendingMentionTargets,
    refreshTopTimerRef,
    removeMessages,
    replyDraft,
    requestRefreshTop,
    resolveEventChatMode,
    resolveEventGroupCount,
    resolveThreadIdByEvent,
    selectedEventId,
    selectedEventIdRef,
    selectedEventInfo,
    selectedThreadIdRef,
    selfReadGuardRef,
    sendEventChatFileMessage,
    sendImages,
    setEvents,
    setIsLoadingSettings,
    setIsSavingSettings,
    setIsUploading,
    setMessages,
    setMobileView,
    setMuteSetting,
    setOffset,
    setReplyDraft,
    setSelectedEventInfo,
    setSettingsEvent,
    setShowNewMsgBanner,
    setShowNotes,
    setShowSettingsModal,
    setSidebarDetails,
    settingsEventIdRef,
    settingsEventRef,
    shouldExcludeAdminSenderForMessage,
    shouldMarkLatestAsRead,
    shouldShowEventMessageSourceBadge,
    showNotes,
    showNotesRef,
    sidebarPressRef,
    socket,
    success,
    toastError,
    updateAdminEventChatNotifyMute,
    updateSidebarAfterSend,
    upsertEventRow,
    useChatDragDrop,
  });

  /* ===== Render ===== */
  // ルートコンテナのクラス（モバイル表示切替）
  const rootClass =
    `admin-event-message-container` +
    (isNarrow ? ' is-mobile' : '') +
    (isNarrow && mobileView === 'list' ? ' is-mobile-list' : '') +
    (isNarrow && mobileView === 'chat' ? ' is-mobile-chat' : '');

  return (
    <div className="admin-dashboard">
      <AdminSidebar />
      <div className={rootClass}>
        <ChatWorkspaceLayout className="admin-event-chat-workspace">
        {/* ======= サイドバー（イベント一覧 + 検索） ======= */}
        <EventChatSidebar
          openTargetSearchModal={openTargetSearchModal}
          searchSummaryLabel={searchSummaryLabel}
          searchToolbarChips={searchToolbarChips}
          assigneeQuickToggles={assigneeQuickToggles}
          messageSearchQuery={messageSearchQuery}
          onClearSearch={clearSearch}
          resultCount={displayedEvents.length}
          hasMoreResults={searchHasMore}
          isSearchLoadingMore={isSearchLoadingMore}
          setEventListNode={setEventListNode}
          handleEventListScroll={handleEventListScroll}
          isBootLoading={isBootLoading}
          isTargetFilterMode={isTargetFilterMode}
          isAiTargetSelected={isAiTargetSelected}
          openAiInbox={openAiInbox}
          followupBadgeCount={followupBadgeCount}
          followupSummary={followupSummary}
          displayedEvents={displayedEvents}
          resolveEventUnread={resolveEventUnread}
          isEventChatAreaOpen={isEventChatAreaOpen}
          selectedEventId={selectedEventId}
          isAdminAssignedToEvent={isAdminAssignedToEvent}
          pendingMentionTargets={pendingMentionTargets}
          resolvingMentionTargetId={resolvingMentionTargetId}
          loadingMentionResolutionTargetKey={loadingMentionResolutionTargetKey}
          openMentionResolution={openMentionResolution}
          resolveMentionTarget={resolveMentionTarget}
          sidebarPressRef={sidebarPressRef}
          clearSidebarContextMenuBlock={clearSidebarContextMenuBlock}
          clearSidebarLongPress={clearSidebarLongPress}
          handleSidebarTouchStart={handleSidebarTouchStart}
          openSettingsModal={openSettingsModal}
          isNarrow={isNarrow}
          setMobileView={setMobileView}
          showNotes={showNotes}
          handleSelectEvent={handleSelectEvent}
          formatEventListTimestamp={formatEventListTimestamp}
          isLoadingEvents={isLoadingEvents}
          hasMoreEvents={hasMoreEvents}
          setLoadMoreNode={setLoadMoreNode}
          toastError={toastError}
          toastSuccess={success}
        />

        <EventChatSearchModal
          isOpen={showSearchModal}
          onClose={() => {
            setShowSearchModal(false);
            setSearchModalError('');
          }}
          onApply={applyTargetSearchModal}
          onClear={clearSearch}
          summary={searchSummaryLabel}
          applying={isSearching}
          errorMessage={searchModalError}
          onRetry={applyTargetSearchModal}
          retrying={isSearching}
          searchDraft={searchDraft}
          setSearchDraft={setSearchDraft}
        />

        <EventMentionSilenceConfirmModal
          confirm={mentionSilenceConfirm}
          onClose={closeMentionSilenceConfirm}
        />

        {/* ======= メイン（ヘッダー常時 + 本文切替） ======= */}
        <div className={`chat-main-with-side${isSidePanelLayoutOpen ? '' : ' is-side-panel-collapsed'}`}>
        <div className="event-chat-area chat-area chat-dnd-scope" ref={eventChatAreaRef}>
          <EventChatHeader
            isVisible={!isAiTargetSelected && Boolean(selectedEventId && selectedEventInfo)}
            isNarrow={isNarrow}
            mobileView={mobileView}
            goToEventList={goToEventList}
            selectedEventFlyerUrl={selectedEventFlyerUrl}
            selectedEventFlyerBackUrl={selectedEventFlyerBackUrl}
            selectedEventInfo={selectedEventInfo}
            handleGoHeaderEventDetail={handleGoHeaderEventDetail}
            onFlyerImageLoad={applyLandscapeContain}
            eventHeaderSchedule={eventHeaderSchedule}
            isSelectedGroupUnified={isSelectedGroupUnified}
            headerNextPerformance={headerNextPerformance}
            isSelectedMultiPerformanceEvent={isSelectedMultiPerformanceEvent}
            headerNextMultiPerformance={headerNextMultiPerformance}
            headerPerformanceType={headerPerformanceType}
            canViewEventNotes={canViewEventNotes}
            setShowNotes={setShowNotes}
            selectedEventId={selectedEventId}
            saveLast={saveLast}
            showNotes={showNotes}
            noteCount={noteCount}
            setShowMembersModal={setShowMembersModal}
            participantCounts={participantCounts}
            chatHeaderMenuOpen={chatHeaderMenuOpen}
            chatHeaderMenuPosition={chatHeaderMenuPosition}
            toggleChatHeaderMenu={toggleChatHeaderMenu}
            canViewMaterials={canViewMaterials}
            materialBadge={materialBadge}
            onOpenMaterials={openMaterialsModal}
            setShowMaterialsModal={setShowMaterialsModal}
            canViewAlbum={canViewAlbum}
            canEditAlbum={canEditAlbum}
            albumBadge={albumBadge}
            onOpenAlbum={openAlbumModal}
            setShowAlbumModal={setShowAlbumModal}
            onOpenLinks={openLinksModal}
            onOpenMedia={openMediaModal}
            assetMenuBadgeTone={assetMenuBadgeTone}
            closeChatHeaderMenu={closeChatHeaderMenu}
            openSettingsModal={openSettingsModal}
            canCreateDerivedGroupChat={canCreateDerivedGroupChat}
            onOpenDerivedGroupChat={() => derivedGroupLauncherRef.current?.open()}
          />

          <EventChatConversation
            isAiTargetSelected={isAiTargetSelected}
            isResolvingInitialChatTarget={isBootLoading && hasInitialChatTarget}
            followupSummary={followupSummary}
            followupRefreshKey={followupRefreshKey}
            handleOpenAiTask={handleOpenAiTask}
            goToEventList={goToEventList}
            isNarrow={isNarrow}
            mobileView={mobileView}
            selectedEventId={selectedEventId}
            latestAnnouncement={latestAnnouncement}
            announcementCount={announcementCount}
            setIsAnnouncementListOpen={setIsAnnouncementListOpen}
            showNotes={showNotes}
            showDelayedInitialLoading={showDelayedInitialLoading}
            isUploading={isUploading}
            showNewMsgBanner={showNewMsgBanner}
            bannerMsg={bannerMsg}
            handleBannerClick={handleBannerClick}
            canViewEventNotes={canViewEventNotes}
            refreshNoteCount={refreshNoteCount}
            canManageEventNotes={canManageEventNotes}
            loggedInAdminId={loggedInAdminId}
            saveLast={saveLast}
            loadingOlderMessages={loadingOlderMessages}
            hasMore={hasMore}
            isDragActive={isDragActive}
            overlayRect={overlayRect}
            displayMessages={displayMessages}
            activeAnchorMessageId={activeAnchorMessageId}
            renderAnnouncementAvatarLeft={renderAnnouncementAvatarLeft}
            isAnnouncementMessage={isAnnouncementMessage}
            isMyAdminMessage={isMyAdminMessage}
            shouldSubtractSelf={shouldSubtractSelf}
            getMainPartLabel={getMainPartLabel}
            getAnnouncementSenderName={getAnnouncementSenderName}
            renderAdminAvatarLeft={renderAdminAvatarLeft}
            renderArtistAvatarLeft={renderArtistAvatarLeft}
            handleAvatarClick={handleAvatarClick}
            inlineCopyMessageId={inlineCopyMessageId}
            handleMessageContextMenu={handleMessageContextMenu}
            handleMessageTouchStart={handleMessageTouchStart}
            clearMessageLongPress={clearMessageLongPress}
            handleJumpToReplyMessage={handleJumpToReplyMessage}
            renderPartialCopyContent={renderPartialCopyContent}
            renderMessageContent={renderMessageContent}
            toggleReaction={toggleReaction}
            openReactionDetail={openReactionDetail}
            isReactionPending={isReactionPending}
            resolveMessageSourceBadgeLabel={resolveMessageSourceBadgeLabel}
            shouldShowMessageSourceBadge={shouldShowMessageSourceBadge}
            withAnnouncementScopeTitle={withAnnouncementScopeTitle}
            shouldShowChatGuidance={shouldShowChatGuidance}
            imgModalOpen={imgModalOpen}
            imageItems={imageItems}
            imgModalIndex={imgModalIndex}
            closeImgModal={closeImgModal}
            showPrevImg={showPrevImg}
            showNextImg={showNextImg}
            messageMenu={messageMenu}
            closeMessageMenu={closeMessageMenu}
            messageMenuRef={messageMenuRef}
            canReactMessage={canReactMessage}
            handleMessageMenuReactionSelect={handleMessageMenuReactionSelect}
            canReplyMessage={canReplyMessage}
            handleSelectReplyMessage={handleSelectReplyMessage}
            canCopyMessage={canCopyMessage}
            handleCopyMessage={handleCopyMessage}
            handleOpenInlinePartialCopy={handleOpenInlinePartialCopy}
            canAnnouncementMessage={canAnnouncementMessage}
            announcementActionMessageId={announcementActionMessageId}
            isAnnouncedMessage={isAnnouncedMessage}
            handleToggleAnnouncementMessage={handleToggleAnnouncementMessage}
            canInspectReadStatus={canInspectReadStatus}
            handleOpenReadStatusDetail={handleOpenReadStatusDetail}
            canUnsendMessage={canUnsendMessage}
            handleUnsendMessage={handleUnsendMessage}
            handleUploading={handleUploading}
            replyDraft={replyDraft}
            setReplyDraft={setReplyDraft}
            mentionCandidates={mentionCandidates}
            handleBeforeEventChatSend={handleBeforeEventChatSend}
            isReadOnly={isReadOnly}
            toastError={toastError}
            updateSidebarAfterSend={updateSidebarAfterSend}
            fetchNewMessages={fetchNewMessages}
            refreshPendingMentionTargets={refreshPendingMentionTargets}
            success={success}
            promptSelfAssignmentAfterSend={promptSelfAssignmentAfterSend}
            chatContainerRef={chatContainerRef}
            showMembersModal={showMembersModal}
            setShowMembersModal={setShowMembersModal}
            selectedEventAdmins={selectedEventAdmins}
            conciergePlanner={conciergePlanner}
            hasConciergeParticipants={hasConciergeParticipants}
            isSelectedGroupUnified={isSelectedGroupUnified}
            selectedEventInfo={selectedEventInfo}
            effectiveGroupEvents={effectiveGroupEvents}
            setSelectedEventInfo={setSelectedEventInfo}
            setGroupPanelEvents={setGroupPanelEvents}
            profileTarget={profileTarget}
            closeProfileModal={closeProfileModal}
            isAnnouncementListOpen={isAnnouncementListOpen}
            announcements={announcements}
            unpinAnnouncement={unpinAnnouncement}
            handleSelectAnnouncementMessage={handleSelectAnnouncementMessage}
            isReactionDetailOpen={isReactionDetailOpen}
            reactionDetailLoading={reactionDetailLoading}
            reactionDetailGroups={reactionDetailGroups}
            closeReactionDetail={closeReactionDetail}
            isReadStatusDetailOpen={isReadStatusDetailOpen}
            readStatusDetailLoading={readStatusDetailLoading}
            readStatusDetailReaders={readStatusDetailReaders}
            closeReadStatusDetail={closeReadStatusDetail}
          />
          </div>

          <EventChatSidePanel
            isVisible={!isAiTargetSelected && Boolean(selectedEventId && selectedEventInfo)}
            isOpen={isSidePanelOpen}
            onCollapse={() => setIsSidePanelOpen(false)}
            onExpand={() => setIsSidePanelOpen(true)}
            eventInfoProps={{
              selectedEventInfo,
              selectedEventFlyerUrl,
              selectedEventFlyerBackUrl,
              selectedEventFlyerFullUrl,
              selectedEventFlyerBackFullUrl,
              onFlyerImageLoad: applyLandscapeContain,
              isSelectedGroupUnified,
              groupEventDetailRows,
              resolveEventDetailPath: (eventUuid) => `/admin/workspaces/event-operations/events/${eventUuid}`,
              eventSchedule,
              scheduleDetail,
              scheduleValueClassName: 'side-info-val side-info-val--venue',
              venueLink,
              venueId: venueIdFromData,
              venueDetailBasePath: '/admin/workspaces/event-operations/venues',
              venueResolvedName,
              venueInfo,
              mapLinkUrl,
              venueResolvedAddress,
              sidebarNote,
              showGroupTaskProgress: canViewEventTasks,
              showGroupCastingProgress: canViewCastingProgress,
              showGroupTicketProgress: canViewTicketSales,
              showTaskProgressBlock: canViewEventTasks,
              showCastingProgressBlock: true,
              showTicketProgressBlock: true,
              taskProgressPercent: taskProgress.percent ?? 0,
              castingPercent: castingPct,
              ticketPercent: ticketProgress.pct,
              isTicketSummaryLoading,
              eventDetailUrl,
            }}
            participantsPanelProps={participantsPanelProps}
          />
        </div>
        </ChatWorkspaceLayout>
        <EventMaterialsModal
          isOpen={showMaterialsModal}
          eventId={selectedEventId}
          canView={canViewMaterials}
          canUpload={canEditMaterials}
          canManage={canEditMaterials} canDownload={canDownloadMaterials}
          canViewAllLogs={canViewMaterialDownloadLogs}
          onClose={() => setShowMaterialsModal(false)}
        />
        <EventAlbumModal
          isOpen={showAlbumModal}
          eventId={selectedEventId}
          eventName={selectedEventInfo?.name || ''}
          eventDateLabel={eventHeaderSchedule?.date || ''}
          canView={canViewAlbum || canEditAlbum}
          canUpload={canEditAlbum}
          canDownload={canDownloadAlbum}
          onClose={() => setShowAlbumModal(false)}
        />
        <ChatLinksModal
          isOpen={showLinksModal}
          loadLinks={chatAssetSource?.loadLinks}
          onClose={() => setShowLinksModal(false)}
        />
        <ChatMediaModal
          isOpen={showMediaModal}
          loadMedia={chatAssetSource?.loadMedia}
          downloadZip={chatAssetSource?.downloadZip} buildFileUrl={chatAssetSource?.buildFileUrl}
          onClose={() => setShowMediaModal(false)}
        />
        <EventChatSettingsModal
          isOpen={showSettingsModal}
          onClose={closeSettingsModal}
          settingsEvent={settingsEvent}
          selectedEventInfo={selectedEventInfo}
          isSettingsGroupUnified={isSettingsGroupUnified}
          isLoadingSettings={isLoadingSettings}
          isSavingSettings={isSavingSettings}
          muteSetting={muteSetting}
          onMuteToggle={toggleMuteImmediately}
        />
        <EventDerivedGroupLauncher ref={derivedGroupLauncherRef} eventId={selectedEventId}
          eventName={selectedEventInfo?.name || selectedEventInfo?.event_name || ''} artists={eventDerivedGroupArtists}
          navigate={navigate} onSuccess={success} onError={toastError} />
        {assignmentConfirmModal}
        <ChatMentionTaskResolutionModal {...mentionResolutionModalProps} />
      </div>
    </div>
  );
}
